/**
 * TimelineView — Google Calendar–style vertical day timeline
 * with hour markers, positioned event blocks, a red "now" line,
 * and drag-to-resize handles for duration editing.
 */

import React, { useEffect, useLayoutEffect, useRef, useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  useWindowDimensions,
  StyleSheet,
  Platform,
  LayoutChangeEvent,
  ScrollView,
} from 'react-native';
import { useTheme, ThemeColors } from '../../utils/theme';
import type { CalendarEvent } from '../../types/api';
import { DateTime } from 'luxon';
import { Ionicons } from '@expo/vector-icons';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { cancelAnimation, useSharedValue, useAnimatedStyle, useDerivedValue, useAnimatedRef, useAnimatedScrollHandler, scrollTo, runOnJS, withDelay, withTiming, withSpring, type SharedValue } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import {
  getScheduleEventColor as getEventColor,
  getScheduleEventFillColor as getEventFillColor,
} from './scheduleAppearance';
import {
  clampScheduleScale,
  getAnchoredScheduleScroll,
  getScheduleTextProgress,
} from './scheduleViewport';

// ── Constants ───────────────────────────────────────────────
const DEFAULT_HOUR_HEIGHT = 60;      // default pixels per hour
const MIN_HOUR_HEIGHT = 30;          // max zoom-out
const MAX_HOUR_HEIGHT = 150;         // max zoom-in
const TOP_PAD = 48;                  // top padding so the first visible hours have breathing room
const GUTTER_WIDTH = 52;             // left column for hour labels
const EVENT_LEFT = GUTTER_WIDTH + 8; // event block left offset
const EVENT_RIGHT = 14;              // right margin
const EVENT_SIDE_INSET = 6;          // keep event lane inside rounded parent edges
const EVENT_GAP = 3;                 // gap between side-by-side columns
const EVENT_MIN_COLUMN_WIDTH = 108;  // keep overlapping reminders readable on narrow screens
const GROUP_POPOVER_MIN_WIDTH = 220;
const GROUP_COMPACT_MIN_H = 22;
const GROUP_LAYOUT_ANIM_MS = 140;
const EVENT_VISUAL_MIN_H = 36;       // minimum touch-friendly height for event blocks
const EVENT_READABLE_MIN_H = 30;     // fixed-size text remains readable at every zoom level
const EVENT_MIN_DURATION_MINUTES = 15;
const EVENT_OVERLAP_WINDOW = 30;     // minutes — events within this window overlap
const NOW_UPDATE_MS = 60_000;        // refresh now-line every 60 s
const INITIAL_TOP_VIEWPORT_GAP = 180; // keep the focus hour lower in the viewport on open
const TIMELINE_BOTTOM_PAD = 176;     // let late-day rows scroll above the floating add button
const HOLD_DRIFT_CANCEL_PX = 22;     // allow tiny finger drift before a long-press gesture activates
const TIMELINE_SWIPE_ACTIVATE_PX = 28;
const TIMELINE_SWIPE_COMMIT_PX = 72;
const TIMELINE_SWIPE_MAX_VERTICAL_PX = 42;
const TIMELINE_SWIPE_AXIS_RATIO = 1.25;
const TIMELINE_SWIPE_MIN_VELOCITY = 650;
const TIMELINE_PAGE_EXIT_MS = 180;

// ── Color logic (mirrors EventChip) ─────────────────────────
// ── Helpers ─────────────────────────────────────────────────
/** Minutes since midnight in local timezone */
function minutesOfDay(isoUtc: string): number {
  const dt = DateTime.fromISO(isoUtc).toLocal();
  return dt.hour * 60 + dt.minute;
}

function formatMinutesToAmPm(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${displayH} ${ampm}` : `${displayH}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatHourLabel(hour: number): string {
  return formatMinutesToAmPm(hour * 60);
}

function snappedDurationFromHeight(height: number, hourHeight: number): number {
  const minHeight = (EVENT_MIN_DURATION_MINUTES / 60) * hourHeight;
  const clampedHeight = Math.max(minHeight, height);
  const rawDuration = Math.round((clampedHeight / hourHeight) * 60);
  const snapped = Math.round(rawDuration / 5) * 5;
  return Math.max(EVENT_MIN_DURATION_MINUTES, Math.min(1440, snapped));
}

function getVisibleEventHeight(durationHeight: number, hourHeight: number, visualGap = 0): number {
  const minDurationHeight = (EVENT_MIN_DURATION_MINUTES / 60) * hourHeight;
  const readableMinHeight = durationHeight < EVENT_VISUAL_MIN_H
    ? Math.max(minDurationHeight, EVENT_READABLE_MIN_H)
    : minDurationHeight;
  const rawHeight = Math.max(readableMinHeight, durationHeight);
  const gap = rawHeight > EVENT_READABLE_MIN_H + visualGap ? visualGap : 0;
  return Math.max(EVENT_READABLE_MIN_H, rawHeight - gap);
}

function getStableLayoutEndMins(startMins: number, actualEndMins: number): number {
  // Packing must be based on time, not pixels. Otherwise crossing a zoom
  // threshold can move a reminder into another column after the gesture ends.
  return Math.min(1440, Math.max(actualEndMins, startMins + EVENT_OVERLAP_WINDOW));
}

function clampHourHeight(value: number): number {
  'worklet';
  return clampScheduleScale(value, MIN_HOUR_HEIGHT, MAX_HOUR_HEIGHT);
}

// ── Isolated NowLine Component ──────────────────────────────
function NowLine({ topPad, totalHeight, hourHeight, animatedHourHeight, zoomContentOffsetUI }: { topPad: number; totalHeight: number; hourHeight: number; animatedHourHeight?: any; zoomContentOffsetUI?: SharedValue<number> }) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const [nowMinutes, setNowMinutes] = useState(() => {
    const n = DateTime.local();
    return n.hour * 60 + n.minute;
  });

  useEffect(() => {
    const id = setInterval(() => {
      const n = DateTime.local();
      setNowMinutes(n.hour * 60 + n.minute);
    }, NOW_UPDATE_MS);
    return () => clearInterval(id);
  }, []);

  const nowLineStyle = useAnimatedStyle(() => ({
    transform: [{
      translateY: topPad
        + (nowMinutes / 60) * (animatedHourHeight ? animatedHourHeight.value : hourHeight)
        + (zoomContentOffsetUI?.value ?? 0),
    }],
  }));

  return (
    <Animated.View
      style={[s.nowLineWrap, { top: 0 }, nowLineStyle]}
      pointerEvents="none"
    >
      <View style={s.nowDot} />
      <View style={s.nowLine} />
    </Animated.View>
  );
}

// ── Props ───────────────────────────────────────────────────
interface Props {
  events: CalendarEvent[];
  allEvents?: CalendarEvent[];
  selectedDate: string;            // YYYY-MM-DD
  onPressEvent?: (event: CalendarEvent) => void;
  onResizeEvent?: (seq: number, durationMinutes: number) => void;
  onMoveEvent?: (seq: number, newRunAtUtc: string) => void;
  onChangeColumn?: (updates: Array<{seq: number; colPref: number}>) => void;
  onDragCreate?: (startMinutes: number, durationMinutes: number) => void;
  onNavigateDay?: (direction: -1 | 1) => void;
  onSwipeNavigationPreview?: (direction: -1 | 1 | null) => void;
  tagColors?: Record<string, string> | null;
  bottomPadding?: number;
  zoomGestureActive?: SharedValue<number>;
}

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);
const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type SwipePreviewLayoutItem = {
  event: CalendarEvent;
  top: number;
  height: number;
  durationHeight: number;
  startMinutes: number;
  endMinutes: number;
  layoutEndMinutes: number;
  color: string;
  col: number;
  totalCols: number;
  groupEvents?: SwipePreviewLayoutItem[];
};

function buildSwipePreviewLayout(
  events: CalendarEvent[],
  hourHeight: number,
  tagColors?: Record<string, string> | null,
  columnOverrides?: Record<number, number>,
): SwipePreviewLayoutItem[] {
  const sourceItems = events
    .filter((event) => !!event.runAtUtc)
    .map((event): SwipePreviewLayoutItem => {
      const startMinutes = minutesOfDay(event.runAtUtc);
      const durationMinutes = (event.durationMinutes ?? 0) > 0
        ? event.durationMinutes!
        : EVENT_OVERLAP_WINDOW;
      const durationHeight = (durationMinutes / 60) * hourHeight;
      const endMinutes = startMinutes + durationMinutes;
      return {
        event,
        top: TOP_PAD + (startMinutes / 60) * hourHeight,
        height: Math.max(EVENT_VISUAL_MIN_H, durationHeight),
        durationHeight,
        startMinutes,
        endMinutes,
        layoutEndMinutes: getStableLayoutEndMins(startMinutes, endMinutes),
        color: getEventColor(event, tagColors),
        col: 0,
        totalCols: 1,
      };
    });

  const sameStartGroups = new Map<number, SwipePreviewLayoutItem[]>();
  sourceItems.forEach((item) => {
    const group = sameStartGroups.get(item.startMinutes) ?? [];
    group.push(item);
    sameStartGroups.set(item.startMinutes, group);
  });

  const groupedItems = Array.from(sameStartGroups.values()).map((group) => {
    if (group.length === 1) return group[0];
    const sortedGroup = [...group].sort((a, b) => {
      const durationDelta = (b.event.durationMinutes ?? 0) - (a.event.durationMinutes ?? 0);
      return durationDelta || (a.event.seq ?? 0) - (b.event.seq ?? 0);
    });
    const representative = sortedGroup[0];
    const endMinutes = Math.max(...sortedGroup.map((item) => item.endMinutes));
    const durationHeight = Math.max(...sortedGroup.map((item) => item.durationHeight));
    return {
      ...representative,
      height: Math.max(EVENT_VISUAL_MIN_H, durationHeight),
      durationHeight,
      endMinutes,
      layoutEndMinutes: getStableLayoutEndMins(representative.startMinutes, endMinutes),
      groupEvents: sortedGroup,
    };
  });

  const sorted = [...groupedItems].sort((a, b) => a.startMinutes - b.startMinutes);
  const overlapGroups: SwipePreviewLayoutItem[][] = [];
  let currentGroup: SwipePreviewLayoutItem[] = [];
  for (const item of sorted) {
    const currentEnd = currentGroup.reduce(
      (latest, current) => Math.max(latest, current.layoutEndMinutes),
      0,
    );
    if (currentGroup.length === 0 || item.startMinutes < currentEnd) currentGroup.push(item);
    else {
      overlapGroups.push(currentGroup);
      currentGroup = [item];
    }
  }
  if (currentGroup.length > 0) overlapGroups.push(currentGroup);

  overlapGroups.forEach((group) => {
    const columns: SwipePreviewLayoutItem[][] = [];
    const ordered = [...group].sort((a, b) => {
      if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
      const preferredA = a.event.seq != null
        ? columnOverrides?.[a.event.seq] ?? a.event.colPref ?? 0
        : 0;
      const preferredB = b.event.seq != null
        ? columnOverrides?.[b.event.seq] ?? b.event.colPref ?? 0
        : 0;
      if (preferredA !== preferredB) return preferredA - preferredB;
      return (a.event.seq ?? 0) - (b.event.seq ?? 0);
    });

    ordered.forEach((item) => {
      const preferred = item.event.seq != null
        ? columnOverrides?.[item.event.seq] ?? item.event.colPref ?? 0
        : 0;
      const startCol = Math.min(Math.max(0, preferred), columns.length);
      while (columns.length <= startCol) columns.push([]);

      let targetCol = -1;
      for (let col = startCol; col < columns.length; col += 1) {
        const overlaps = columns[col].some((other) => (
          item.startMinutes < other.layoutEndMinutes
          && item.layoutEndMinutes > other.startMinutes
        ));
        if (!overlaps) {
          targetCol = col;
          break;
        }
      }
      if (targetCol < 0) {
        for (let col = 0; col < startCol; col += 1) {
          const overlaps = columns[col].some((other) => (
            item.startMinutes < other.layoutEndMinutes
            && item.layoutEndMinutes > other.startMinutes
          ));
          if (!overlaps) {
            targetCol = col;
            break;
          }
        }
      }
      if (targetCol < 0) {
        targetCol = columns.length;
        columns.push([]);
      }
      columns[targetCol].push(item);
      item.col = targetCol;
    });
    group.forEach((item) => { item.totalCols = columns.length; });
  });

  return groupedItems;
}

function SwipePreviewEventCard({
  item,
  left,
  width,
  animatedHourHeight,
  styles,
}: {
  item: SwipePreviewLayoutItem;
  left: number;
  width: number;
  animatedHourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) {
  const durationMinutes = (item.event.durationMinutes ?? 0) > 0
    ? item.event.durationMinutes!
    : EVENT_OVERLAP_WINDOW;
  const start = DateTime.fromISO(item.event.runAtUtc).toLocal();
  const end = start.plus({ minutes: durationMinutes });
  const positionStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    return {
      top: TOP_PAD + (item.startMinutes / 60) * animatedHourHeight.value,
      height: Math.max(EVENT_VISUAL_MIN_H, durationHeight),
    };
  });
  const innerStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    return { height: Math.max(EVENT_READABLE_MIN_H, durationHeight) };
  });
  const titleStyle = useAnimatedStyle(() => {
    const durationHeight = Math.max(
      EVENT_READABLE_MIN_H,
      (durationMinutes / 60) * animatedHourHeight.value,
    );
    const progress = getScheduleTextProgress(durationHeight, EVENT_READABLE_MIN_H, 66);
    return {
      fontSize: 9.5 + progress * 2.5,
      lineHeight: 11.5 + progress * 3.5,
    };
  });
  const timeStyle = useAnimatedStyle(() => {
    const durationHeight = Math.max(
      EVENT_READABLE_MIN_H,
      (durationMinutes / 60) * animatedHourHeight.value,
    );
    const progress = getScheduleTextProgress(durationHeight, EVENT_READABLE_MIN_H, 66);
    return {
      fontSize: 8 + progress * 2,
      lineHeight: 9.5 + progress * 2.5,
      opacity: 0.78 + progress * 0.22,
    };
  });

  return (
    <Animated.View
      style={[
        styles.swipePreviewPositionedEvent,
        { left, width },
        positionStyle,
      ]}
    >
      <Animated.View
        style={[
          styles.swipePreviewEventInner,
          {
            backgroundColor: getEventFillColor(item.color),
            borderLeftColor: item.color,
          },
          innerStyle,
        ]}
      >
        <View style={styles.eventContentStable}>
          <Animated.Text
            style={[styles.eventTitleCompact, { color: item.color }, titleStyle]}
            numberOfLines={1}
          >
            {item.event.body}
          </Animated.Text>
          <Animated.Text style={[styles.eventTime, timeStyle]} numberOfLines={1}>
            {start.toFormat('h:mm a')}{item.event.durationMinutes ? ` \u2013 ${end.toFormat('h:mm a')}` : ''}
          </Animated.Text>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

function SwipePreviewGroupCard({
  item,
  left,
  width,
  animatedHourHeight,
  styles,
}: {
  item: SwipePreviewLayoutItem;
  left: number;
  width: number;
  animatedHourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) {
  const groupEvents = item.groupEvents ?? [item];
  const durationMinutes = Math.max(
    ...groupEvents.map((groupItem) => groupItem.event.durationMinutes || EVENT_MIN_DURATION_MINUTES),
  );
  const narrow = width < 150;
  const veryNarrow = width < 118;
  const horizontalPadding = veryNarrow ? 5 : narrow ? 6 : 8;
  const badgeSize = veryNarrow ? 16 : narrow ? 18 : 20;
  const titles = groupEvents.slice(0, 2).map((groupItem) => groupItem.event.body).join(', ');
  const moreCount = Math.max(0, groupEvents.length - 2);

  const positionStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    return {
      top: TOP_PAD + (item.startMinutes / 60) * animatedHourHeight.value,
      height: Math.max(EVENT_VISUAL_MIN_H, durationHeight),
    };
  });
  const innerStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    const blockHeight = Math.max(EVENT_VISUAL_MIN_H, durationHeight);
    const visibleHeight = Math.min(
      blockHeight,
      Math.max(GROUP_COMPACT_MIN_H, durationHeight - EVENT_GAP),
    );
    const progress = getScheduleTextProgress(visibleHeight, GROUP_COMPACT_MIN_H, 62);
    return {
      height: visibleHeight,
      paddingVertical: 2 + progress * 3,
    };
  });
  const titleStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    const liveHeight = Math.max(GROUP_COMPACT_MIN_H, durationHeight - EVENT_GAP);
    const progress = getScheduleTextProgress(liveHeight, GROUP_COMPACT_MIN_H, 62);
    const widthPenalty = veryNarrow ? 1.5 : narrow ? 0.5 : 0;
    return {
      fontSize: 9.5 + progress * 1.5 - widthPenalty,
      lineHeight: 11.5 + progress * 2.5 - widthPenalty * 0.5,
    };
  });
  const timeStyle = useAnimatedStyle(() => {
    const durationHeight = (durationMinutes / 60) * animatedHourHeight.value;
    const liveHeight = Math.max(GROUP_COMPACT_MIN_H, durationHeight - EVENT_GAP);
    const progress = getScheduleTextProgress(liveHeight, 38, 58);
    return {
      fontSize: 8 + progress * 2,
      lineHeight: 10 + progress * 2,
      opacity: progress,
    };
  });

  return (
    <Animated.View
      style={[
        styles.swipePreviewPositionedEvent,
        { left, width },
        positionStyle,
      ]}
    >
      <Animated.View
        style={[
          styles.swipePreviewGroupInner,
          {
            paddingHorizontal: horizontalPadding,
            backgroundColor: getEventFillColor(item.color),
            borderLeftColor: item.color,
          },
          innerStyle,
        ]}
      >
        <View style={[styles.groupHeaderRow, { gap: veryNarrow ? 4 : 6 }]}>
          <Animated.Text
            style={[styles.groupTitle, { color: item.color }, titleStyle]}
            numberOfLines={1}
          >
            {titles}{moreCount > 0 ? ` +${moreCount}` : ''}
          </Animated.Text>
          <View
            style={[
              styles.groupCountBadge,
              {
                minWidth: badgeSize,
                height: badgeSize,
                borderRadius: badgeSize / 2,
                backgroundColor: item.color,
              },
            ]}
          >
            <Text style={styles.groupCountText}>{groupEvents.length}</Text>
          </View>
        </View>
        {width >= 118 ? (
          <Animated.Text
            style={[
              styles.swipePreviewGroupTime,
              { left: horizontalPadding },
              timeStyle,
            ]}
            numberOfLines={1}
          >
            {formatMinutesToAmPm(item.startMinutes)}
          </Animated.Text>
        ) : null}
      </Animated.View>
    </Animated.View>
  );
}

const TimelineSwipePreview = React.memo(function TimelineSwipePreview({
  events,
  hourHeight,
  animatedHourHeight,
  totalHeight,
  width,
  tagColors,
  columnOverrides,
  styles,
  pageStyle,
  contentOffsetStyle,
}: {
  events: CalendarEvent[];
  hourHeight: number;
  animatedHourHeight: SharedValue<number>;
  totalHeight: number;
  width: number;
  tagColors?: Record<string, string> | null;
  columnOverrides?: Record<number, number>;
  styles: ReturnType<typeof createStyles>;
  pageStyle: any;
  contentOffsetStyle: any;
}) {
  const layout = useMemo(
    () => buildSwipePreviewLayout(events, hourHeight, tagColors, columnOverrides),
    [columnOverrides, events, hourHeight, tagColors],
  );
  const maxColumns = layout.reduce((max, item) => Math.max(max, item.totalCols), 1);
  const previewContentWidth = Math.ceil(Math.max(
    width,
    EVENT_LEFT + EVENT_RIGHT + EVENT_SIDE_INSET * 2
      + maxColumns * EVENT_MIN_COLUMN_WIDTH
      + Math.max(0, maxColumns - 1) * EVENT_GAP,
  ));

  return (
    <Animated.View
      pointerEvents="none"
      renderToHardwareTextureAndroid
      shouldRasterizeIOS
      style={[styles.swipePreviewViewport, pageStyle]}
    >
      <Animated.View
        style={[
          styles.swipePreviewContent,
          { height: totalHeight, width: previewContentWidth },
          contentOffsetStyle,
        ]}
      >
        <Animated.View style={[styles.hourGrid, { height: 24 * hourHeight }]} pointerEvents="none">
          <View style={styles.hourGridSurface}>
            {Array.from({ length: 24 }, (_, hour) => (
              <HourLine
                key={hour}
                h={hour}
                animatedHourHeight={animatedHourHeight}
                styles={styles}
              />
            ))}
          </View>
          <HourLabels
            animatedHourHeight={animatedHourHeight}
            styles={styles}
          />
        </Animated.View>
        {layout.map((item, index) => {
          const visibleTotalWidth = Math.max(0, width - EVENT_LEFT - EVENT_RIGHT - EVENT_SIDE_INSET * 2);
          const readableTotalWidth = item.totalCols * EVENT_MIN_COLUMN_WIDTH
            + Math.max(0, item.totalCols - 1) * EVENT_GAP;
          const laneWidth = Math.max(visibleTotalWidth, readableTotalWidth);
          const colWidth = (laneWidth - Math.max(0, item.totalCols - 1) * EVENT_GAP) / item.totalCols;
          const left = EVENT_LEFT + EVENT_SIDE_INSET + item.col * (colWidth + EVENT_GAP);

          return item.groupEvents ? (
            <SwipePreviewGroupCard
              key={`preview-group-${item.startMinutes}-${index}`}
              item={item}
              left={left}
              width={colWidth}
              animatedHourHeight={animatedHourHeight}
              styles={styles}
            />
          ) : (
            <SwipePreviewEventCard
              key={item.event.seq != null ? `preview-${item.event.seq}` : `preview-${item.event.runAtUtc}-${index}`}
              item={item}
              left={left}
              width={colWidth}
              animatedHourHeight={animatedHourHeight}
              styles={styles}
            />
          );
        })}
      </Animated.View>
    </Animated.View>
  );
});

// ── Single event block with animated resize + drag ──────────
const HourLabels = React.memo(({ animatedHourHeight, styles }: {
  animatedHourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) => {
  const spacingStyle = useAnimatedStyle(() => ({
    lineHeight: animatedHourHeight.value,
    transform: [{ translateY: -(animatedHourHeight.value - 10) / 2 }],
  }));
  return (
    <Animated.Text style={[styles.hourLabelsText, { lineHeight: DEFAULT_HOUR_HEIGHT }, spacingStyle]}>
      {Array.from({ length: 24 }, (_, hour) => formatHourLabel(hour)).join('\n')}
    </Animated.Text>
  );
});

const HourLine = React.memo(({ h, animatedHourHeight, styles }: {
  h: number;
  animatedHourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) => {
  const positionStyle = useAnimatedStyle(() => ({
    height: animatedHourHeight.value,
    transform: [{ translateY: h * animatedHourHeight.value }],
  }));

  return (
    <Animated.View style={[styles.hourRow, { top: 0, height: DEFAULT_HOUR_HEIGHT }, positionStyle]}>
      <View style={styles.hourLineGutter} />
      <View style={styles.hourLine} />
    </Animated.View>
  );
});

type PositionedEventItem = {
  event: CalendarEvent;
  top: number;
  height: number;
  durationHeight: number;
  originalMins: number;
  originalEndMins: number;
  layoutEndMins: number;
  color: string;
  origCol: number;
  origTotalCols: number;
  dragCol: number;
  dragTotalCols: number;
  mins?: number;
  endMins?: number;
  col?: number;
  totalCols?: number;
  isGroup?: boolean;
  groupKey?: string;
  groupEvents?: PositionedEventItem[];
  isDragClone?: boolean;
};

interface EventBlockProps {
  p: PositionedEventItem;
  colLeft: number;
  colW: number;
  isSelected: boolean;
  onSelect: (seq: number | undefined) => void;
  onPress?: (event: CalendarEvent) => void;
  onResizeEvent?: (seq: number, durationMinutes: number) => void;
  onMoveEvent?: (seq: number, newRunAtUtc: string) => void;
  onMoveToMinutes?: (event: CalendarEvent, minutes: number) => void;
  onDragUpdate?: (seq: number, mins: number, hoverCol?: number) => void;
  onDragClear?: (dropCol?: number) => void;
  onDragStart?: (event: CalendarEvent) => void;
  onDragEdge?: (absoluteX: number, absoluteY: number, allowDaySwitch?: boolean) => void;
  onDragEdgeStop?: () => void;
  scrollOffsetUI: { value: number };
  scrollNativeGesture?: any;
  selectedDate: string;
  hourHeight: number;
  animatedHourHeight?: any;
  zoomContentOffsetUI?: SharedValue<number>;
  totalHeight: number;
}

function EventBlock({ p, colLeft, colW, isSelected, onSelect, onPress, onResizeEvent, onMoveEvent, onMoveToMinutes, onDragUpdate, onDragClear, onDragStart, onDragEdge, onDragEdgeStop, scrollOffsetUI, scrollNativeGesture, selectedDate, hourHeight, totalHeight, animatedHourHeight, zoomContentOffsetUI }: EventBlockProps) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const heightDelta = useSharedValue(0);
  const translateY = useSharedValue(0);
  const translateX = useSharedValue(0);
  const isDragging = useSharedValue(0);
  const frozenLeft = useSharedValue(colLeft);
  const frozenWidth = useSharedValue(colW);
  const animatedLeft = useSharedValue(colLeft);
  const animatedWidth = useSharedValue(colW);
  const latestTop = useSharedValue(p.top);
  const latestColLeft = useSharedValue(colLeft);
  const latestColW = useSharedValue(colW);
  const latestTotalCols = useSharedValue(p.totalCols || 1);
  const dragStartTop = useSharedValue(p.top);
  const dragStartLeft = useSharedValue(colLeft);
  const dragStartWidth = useSharedValue(colW);
  const dragStartTotalCols = useSharedValue(p.totalCols || 1);
  const scrollAtDragStart = useSharedValue(0);
  const selectedDateRef = useRef(selectedDate);
  const eventRef = useRef(p.event);
  const [dragTimeLabel, setDragTimeLabel] = useState<string | null>(null);
  const [dragSnapMins, setDragSnapMins] = useState<number | null>(null);
  const [resizeTimeLabel, setResizeTimeLabel] = useState<string | null>(null);
  const justDragged = useRef(false);

  const markDragged = useCallback(() => { justDragged.current = true; }, []);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    eventRef.current = p.event;
    latestTop.value = p.top;
  }, [latestTop, p.event, p.top]);

  useEffect(() => {
    latestColLeft.value = colLeft;
    latestColW.value = colW;
    latestTotalCols.value = p.totalCols || 1;
    animatedLeft.value = withSpring(colLeft, { damping: 20, stiffness: 200, mass: 1 });
    animatedWidth.value = withSpring(colW, { damping: 20, stiffness: 200, mass: 1 });
  }, [animatedLeft, animatedWidth, colLeft, colW, latestColLeft, latestColW, latestTotalCols, p.totalCols]);

  const triggerHaptic = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  }, []);

  const updateDragLabel = useCallback((mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const startDt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m });
    const endDt = startDt.plus({ minutes: p.event.durationMinutes || 0 });
    setDragTimeLabel(`${formatMinutesToAmPm(mins)} – ${endDt.toFormat('h:mm a')}`);
  }, [p.event.durationMinutes]);

  const clearDragLabel = useCallback(() => { 
    setDragTimeLabel(null); 
    setDragSnapMins(null);
  }, []);

  const notifyDragStart = useCallback(() => {
    if (onDragStart) onDragStart(eventRef.current);
  }, [onDragStart]);

  const handleMoveEnd = useCallback((newMins: number) => {
    const movingEvent = eventRef.current;
    if (onMoveToMinutes) {
      onMoveToMinutes(movingEvent, newMins);
      return;
    }
    if (!onMoveEvent || movingEvent.seq == null) return;
    const h = Math.floor(newMins / 60);
    const m = newMins % 60;
    const newDt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m }).toUTC().toISO();
    if (newDt) onMoveEvent(movingEvent.seq, newDt);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }, [onMoveEvent, onMoveToMinutes]);

  // Snap helper — called via runOnJS from gesture callbacks
  const lastSnappedTime = useRef<number | null>(null);
  const lastSnappedCol = useRef<number | null>(null);
  const handleDragSnap = useCallback((ty: number, tx: number, baseTop: number, baseLeft: number, baseWidth: number, baseTotalCols: number) => {
    const newY = baseTop + ty;
    const rawMins = ((newY - TOP_PAD) / hourHeight) * 60;
    const snapped = Math.round(Math.max(0, Math.min(1439, rawMins)) / 5) * 5;

    const rawX = baseLeft + tx - EVENT_LEFT;
    const totalAvail = baseWidth * Math.max(1, baseTotalCols);
    const estimatedW = totalAvail / 2;
    const hoverCol = rawX > estimatedW * 0.5 ? 1 : 0;

    if (lastSnappedTime.current !== snapped || lastSnappedCol.current !== hoverCol) {
      lastSnappedTime.current = snapped;
      lastSnappedCol.current = hoverCol;
      updateDragLabel(snapped);
      if (onDragUpdate && p.event.seq != null) onDragUpdate(p.event.seq, snapped, hoverCol);
    }
  }, [p.event.seq, updateDragLabel, onDragUpdate, hourHeight]);

  const handleDragEnd = useCallback((ty: number, tx: number, baseTop: number, baseLeft: number, baseWidth: number, baseTotalCols: number) => {
    const newY = baseTop + ty;
    const rawMins = ((newY - TOP_PAD) / hourHeight) * 60;
    const snapped = Math.round(Math.max(0, Math.min(1439, rawMins)) / 5) * 5;
    handleMoveEnd(snapped);
    clearDragLabel();

    const rawX = baseLeft + tx - EVENT_LEFT;
    const totalAvail = baseWidth * Math.max(1, baseTotalCols);
    const estimatedW = totalAvail / 2;
    const dropCol = rawX > estimatedW * 0.5 ? 1 : 0;

    if (onDragClear) onDragClear(dropCol);
  }, [handleMoveEnd, clearDragLabel, onDragClear, hourHeight]);

  const handleResizeEnd = useCallback((newHeight: number) => {
    heightDelta.value = 0;
    if (!p.event.seq || !onResizeEvent) return;
    onResizeEvent(p.event.seq, snappedDurationFromHeight(newHeight, hourHeight));
  }, [p.event.seq, onResizeEvent, heightDelta, hourHeight]);

  const handleTap = useCallback(() => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    if (isSelected) {
      onPress?.(p.event);
    } else {
      onSelect(p.event.seq);
    }
  }, [isSelected, onPress, onSelect, p.event]);

  // Helper to create the drag pan gesture
  const createDragPan = (withLongPress: boolean) => {
    let pan = Gesture.Pan()
      .onStart(() => {
        isDragging.value = 1;
        dragStartTop.value = latestTop.value;
        dragStartLeft.value = latestColLeft.value;
        dragStartWidth.value = latestColW.value;
        dragStartTotalCols.value = latestTotalCols.value;
        frozenLeft.value = latestColLeft.value;
        frozenWidth.value = latestColW.value;
        scrollAtDragStart.value = scrollOffsetUI.value;
        if (p.event.seq != null) runOnJS(onSelect)(p.event.seq);
        runOnJS(markDragged)();
        runOnJS(triggerHaptic)();
        runOnJS(notifyDragStart)();
      })
      .onUpdate((e) => {
        const scrollDelta = scrollOffsetUI.value - scrollAtDragStart.value;
        const effectiveY = e.translationY + scrollDelta;
        translateX.value = e.translationX;
        translateY.value = effectiveY;
        runOnJS(handleDragSnap)(effectiveY, e.translationX, dragStartTop.value, dragStartLeft.value, dragStartWidth.value, dragStartTotalCols.value);
        if (onDragEdge) runOnJS(onDragEdge)(e.absoluteX, e.absoluteY, true);
      })
      .onEnd((e) => {
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        const scrollDelta = scrollOffsetUI.value - scrollAtDragStart.value;
        const effectiveY = e.translationY + scrollDelta;
        runOnJS(handleDragEnd)(effectiveY, e.translationX, dragStartTop.value, dragStartLeft.value, dragStartWidth.value, dragStartTotalCols.value);
        translateY.value = withTiming(0, { duration: 150 });
        translateX.value = withTiming(0, { duration: 150 });
      })
      .onFinalize((_, success) => {
        isDragging.value = 0;
        translateY.value = withTiming(0, { duration: 150 });
        translateX.value = withTiming(0, { duration: 150 });
        runOnJS(clearDragLabel)();
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        if (onDragClear) runOnJS(onDragClear)();
      });
      
    if (withLongPress) {
      pan = pan
        .activateAfterLongPress(260)
        .failOffsetX([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
        .failOffsetY([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX]);
    } else {
      // Without a long-press guard, require a minimum movement delta so that a
      // quick tap doesn't activate the Pan and set justDragged = true before
      // the Pressable can fire — which was eating every first tap on a selected event.
      pan = pan.activeOffsetX([-8, 8]).activeOffsetY([-8, 8]);
    }
    if (scrollNativeGesture) {
      pan = pan.blocksExternalGesture(scrollNativeGesture);
    }
    return pan;
  };

  // Drag gesture: move to a new time slot
  const dragGesture = useMemo(() => createDragPan(true), 
  [isSelected, translateY, translateX, scrollAtDragStart, scrollOffsetUI, scrollNativeGesture, p.event.seq, onSelect, triggerHaptic, markDragged, handleDragSnap, handleDragEnd, clearDragLabel, onDragClear, notifyDragStart, onDragEdge, onDragEdgeStop]);

  const updateResizeLabel = useCallback((newHeight: number) => {
    const snappedDuration = snappedDurationFromHeight(newHeight, hourHeight);
    const startDt = DateTime.fromISO(p.event.runAtUtc).toLocal();
    const endDt = startDt.plus({ minutes: snappedDuration });
    setResizeTimeLabel(`${startDt.toFormat('h:mm a')} – ${endDt.toFormat('h:mm a')}`);
  }, [p.event.runAtUtc, hourHeight]);

  const clearResizeLabel = useCallback(() => setResizeTimeLabel(null), []);

  // Resize gesture: change duration (instant swipe)
  const resizeGesture = useMemo(() =>
    Gesture.Pan()
      .activeOffsetY([-5, 5])
      .onStart(() => { runOnJS(markDragged)(); })
      .onUpdate((e) => { 
        heightDelta.value = e.translationY; 
        runOnJS(updateResizeLabel)(p.durationHeight + e.translationY);
      })
      .onEnd(() => {
        const final = p.durationHeight + heightDelta.value;
        runOnJS(handleResizeEnd)(final);
      })
      .onFinalize(() => { 
        heightDelta.value = 0; 
        runOnJS(clearResizeLabel)();
      }),
  [p.durationHeight, heightDelta, handleResizeEnd, markDragged, updateResizeLabel, clearResizeLabel]);

  // Handle drag gesture: drag event if user long-presses the bottom handle instead of swiping
  const handleDragGesture = useMemo(() => {
    return createDragPan(true);
  }, [translateY, translateX, scrollAtDragStart, scrollOffsetUI, scrollNativeGesture, p.event.seq, onSelect, triggerHaptic, markDragged, handleDragSnap, handleDragEnd, clearDragLabel, onDragClear, notifyDragStart, onDragEdge, onDragEdgeStop]);

  // Tap on the bottom handle area → open edit (e.g. small events where the handle covers most of the block)
  const resizeAreaTap = useMemo(() =>
    Gesture.Tap().onEnd((_e, success) => { if (success) runOnJS(handleTap)(); }),
  [handleTap]);

  // Combine bottom gestures: tap to open edit, swipe to resize, long-press to drag
  const bottomBarGesture = useMemo(() => Gesture.Race(resizeAreaTap, handleDragGesture, resizeGesture), [resizeAreaTap, handleDragGesture, resizeGesture]);

  const isDone = !!p.event.sent;
  const strikeStyle = isDone ? { textDecorationLine: 'line-through' as const } : undefined;
  const titleTypographyStyle = useAnimatedStyle(() => {
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const durationMinutes = (p.event.durationMinutes ?? 0) > 0
      ? p.event.durationMinutes!
      : EVENT_OVERLAP_WINDOW;
    const liveHeight = Math.max(
      EVENT_READABLE_MIN_H,
      (durationMinutes / 60) * zoomH + heightDelta.value,
    );
    const progress = getScheduleTextProgress(liveHeight, EVENT_READABLE_MIN_H, 66);
    return {
      fontSize: 9.5 + progress * 2.5,
      lineHeight: 11.5 + progress * 3.5,
    };
  });
  const timeTypographyStyle = useAnimatedStyle(() => {
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const durationMinutes = (p.event.durationMinutes ?? 0) > 0
      ? p.event.durationMinutes!
      : EVENT_OVERLAP_WINDOW;
    const liveHeight = Math.max(
      EVENT_READABLE_MIN_H,
      (durationMinutes / 60) * zoomH + heightDelta.value,
    );
    const progress = getScheduleTextProgress(liveHeight, EVENT_READABLE_MIN_H, 66);
    return {
      fontSize: 8 + progress * 2,
      lineHeight: 9.5 + progress * 2.5,
      opacity: 0.78 + progress * 0.22,
    };
  });
  const textContent = (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <Animated.Text
          style={[s.eventTitleCompact, { color: p.color, flexShrink: 1 }, strikeStyle, titleTypographyStyle]}
          numberOfLines={1}
        >
          {p.event.body}
        </Animated.Text>
        {p.event.source === 'google' ? <Ionicons name="logo-google" size={11} color="#EA4335" accessibilityLabel="From Google Calendar" /> : p.event.googleEventId ? <Ionicons name="logo-google" size={11} color="#4285F4" accessibilityLabel="Synced to Google Calendar" /> : null}
      </View>
      <Animated.Text style={[s.eventTime, timeTypographyStyle]} numberOfLines={1}>
        {DateTime.fromISO(p.event.runAtUtc).toLocal().toFormat('h:mm a')}
        {p.event.durationMinutes ? ` – ${DateTime.fromISO(p.event.runAtUtc).toLocal().plus({ minutes: p.event.durationMinutes }).toFormat('h:mm a')}` : ''}
      </Animated.Text>
    </>
  );

  const contentStyle = s.eventContentStable;

  const animatedStyle = useAnimatedStyle(() => {
      const dragging = isDragging.value === 1;
      let topVal = typeof p.top === 'number' ? p.top : 0;
      const zoomH = animatedHourHeight?.value ?? hourHeight;
      const durationMinutes = (p.event.durationMinutes ?? 0) > 0
        ? p.event.durationMinutes!
        : EVENT_OVERLAP_WINDOW;
      const heightVal = (durationMinutes / 60) * zoomH;
      
      if (animatedHourHeight) {
        topVal = TOP_PAD + (p.originalMins / 60) * animatedHourHeight.value;
      }

      const minDurationHeight = (EVENT_MIN_DURATION_MINUTES / 60) * zoomH;
      const visibleHeight = Math.max(minDurationHeight, heightVal + heightDelta.value);
      
      return {
        height: Math.max(EVENT_VISUAL_MIN_H, visibleHeight),
        // Horizontal layout only animates when the overlap layout actually
        // changes, rather than restarting springs on every pinch frame.
        left: dragging ? frozenLeft.value : animatedLeft.value,
        width: dragging ? frozenWidth.value : animatedWidth.value,
        transform: [
          { translateX: translateX.value },
          { translateY: translateY.value + topVal - TOP_PAD + (zoomContentOffsetUI?.value ?? 0) },
        ],
      };
    });

  const visibleBlockAnimatedStyle = useAnimatedStyle(() => {
      const zoomH = animatedHourHeight?.value ?? hourHeight;
      const durationMinutes = (p.event.durationMinutes ?? 0) > 0
        ? p.event.durationMinutes!
        : EVENT_OVERLAP_WINDOW;
      const heightVal = (durationMinutes / 60) * zoomH;

      const minDurationHeight = (EVENT_MIN_DURATION_MINUTES / 60) * zoomH;
      const readableMinHeight = Math.max(minDurationHeight, EVENT_READABLE_MIN_H);
      return { height: Math.max(readableMinHeight, heightVal + heightDelta.value) };
    });

  const resizeHandleAnimatedStyle = useAnimatedStyle(() => {
      const zoomH = animatedHourHeight?.value ?? hourHeight;
      const durationMinutes = (p.event.durationMinutes ?? 0) > 0
        ? p.event.durationMinutes!
        : EVENT_OVERLAP_WINDOW;
      const heightVal = (durationMinutes / 60) * zoomH;

      const minDurationHeight = (EVENT_MIN_DURATION_MINUTES / 60) * zoomH;
      const readableMinHeight = Math.max(minDurationHeight, EVENT_READABLE_MIN_H);
      const visibleHeight = Math.max(readableMinHeight, heightVal + heightDelta.value);
      return { top: Math.max(0, visibleHeight - 7) };
    });

  const dynamicZIndex = isSelected ? 50 : dragTimeLabel ? 40 : 1;

  return (
    <>
      {dragTimeLabel && (
         <View style={[
           s.eventBlock,
           {
             top: dragSnapMins !== null ? (dragSnapMins / 60) * hourHeight + TOP_PAD : p.top,
             left: colLeft,
             width: colW,
             height: p.height,
             /* minH logic inside animatedStyle */
             borderLeftColor: p.color,
             borderLeftWidth: 3,
             backgroundColor: p.color + '18',
             opacity: 0.3,
             overflow: 'hidden',
           }
         ]}>
           <View style={contentStyle}>{textContent}</View>
         </View>
      )}
      <GestureDetector gesture={dragGesture}>
        <Animated.View
          // @ts-ignore — dataSet is a react-native-web extension for data-* attrs
          dataSet={{ eventBlock: 'true' }}
          style={[
          s.eventBlock,
          isSelected ? s.eventBlockSelectedCompact : null,
          {
            // Use one stable native origin. The animated transform is the
            // sole source of vertical geometry, so a React commit cannot race
            // Reanimated and place the reminder at an intermediate position.
            top: TOP_PAD,
            /* touch target height logic inside animatedStyle */
            borderLeftWidth: 0,
            zIndex: dynamicZIndex,
          },
          animatedStyle,
        ]}>
          <Animated.View style={[
            s.eventBlockInner,
            visibleBlockAnimatedStyle,
            {
              backgroundColor: isSelected ? '#FFFFFF' : getEventFillColor(p.color),
              borderColor: isSelected ? p.color : 'transparent',
              borderWidth: isSelected ? 2 : 0,
              borderLeftColor: p.color,
              borderLeftWidth: isSelected ? 4 : 3,
            },
          ]}>
            {isSelected && (
               <View style={[StyleSheet.absoluteFill, { backgroundColor: p.color + '25', borderRadius: 8, borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }]} pointerEvents="none" />
            )}
            <Pressable onPress={handleTap} style={contentStyle}>
              {textContent}
            </Pressable>
          </Animated.View>
          {isSelected && onResizeEvent && p.event.seq != null && (
            <GestureDetector gesture={bottomBarGesture}>
              <Animated.View style={[s.resizeHandle, resizeHandleAnimatedStyle]}>
                <View style={[s.resizeGrip, { backgroundColor: p.color }]} />
              </Animated.View>
            </GestureDetector>
          )}
          {dragTimeLabel && (
            <View style={s.dragTimeLabel}>
              <Text style={s.dragTimeLabelText}>{dragTimeLabel}</Text>
            </View>
          )}
          {resizeTimeLabel && (
            <View style={s.dragTimeLabel}>
              <Text style={s.dragTimeLabelText}>{resizeTimeLabel}</Text>
            </View>
          )}
        </Animated.View>
      </GestureDetector>
    </>
  );
}

function DragPreviewEventBlock({ p, colLeft, colW }: { p: PositionedEventItem; colLeft: number; colW: number }) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const isDone = !!p.event.sent;
  const strikeStyle = isDone ? { textDecorationLine: 'line-through' as const } : undefined;
  const startTime = DateTime.fromISO(p.event.runAtUtc).toLocal();
  const endTime = startTime.plus({ minutes: p.event.durationMinutes || EVENT_MIN_DURATION_MINUTES });
  const usesReadableOverlay = p.durationHeight < EVENT_VISUAL_MIN_H;
  const visibleHeight = Math.max(EVENT_VISUAL_MIN_H, p.durationHeight);
  const readableHeight = usesReadableOverlay ? Math.max(visibleHeight, EVENT_READABLE_MIN_H) : visibleHeight;

  const textContent = p.durationHeight < 20 ? (
    <Text style={[s.eventTitleTiny, { color: p.color }, strikeStyle]} numberOfLines={1}>
      {p.event.body}
    </Text>
  ) : p.durationHeight < 45 ? (
    <Text style={[s.eventTitleCompact, { color: p.color }, strikeStyle]} numberOfLines={1}>
      {p.event.body}
      <Text style={s.eventTimeInline}>
        {' · '}{startTime.toFormat('h:mm a')}
      </Text>
    </Text>
  ) : (
    <>
      <Text style={[s.eventTitle, { color: p.color }, strikeStyle]} numberOfLines={1}>
        {p.event.body}
      </Text>
      <Text style={s.eventTime}>
        {startTime.toFormat('h:mm a')}
        {p.event.durationMinutes ? ` – ${endTime.toFormat('h:mm a')}` : ''}
      </Text>
    </>
  );

  const contentStyle = p.durationHeight < 20 ? s.eventContentTiny : p.durationHeight < 45 ? s.eventContentCompact : s.eventContent;

  return (
    <View
      // @ts-ignore - dataSet is a react-native-web extension for data-* attrs
      dataSet={{ eventBlock: 'true' }}
      pointerEvents="none"
      style={[
        s.eventBlock,
        s.eventBlockSelectedCompact,
        {
          top: p.top,
          left: colLeft,
          width: colW,
          height: visibleHeight,
          borderLeftWidth: 0,
          zIndex: 98,
        },
      ]}
    >
      <View
        style={[
          s.eventBlockInner,
          contentStyle,
          {
            height: readableHeight,
            backgroundColor: getEventFillColor(p.color),
            borderLeftColor: p.color,
            borderLeftWidth: 3,
          },
        ]}
      >
        {textContent}
      </View>
      <View style={s.dragTimeLabel} pointerEvents="none">
        <Text style={s.dragTimeLabelText}>{formatMinutesToAmPm(p.originalMins)} - {formatMinutesToAmPm(Math.min(1440, p.originalEndMins))}</Text>
      </View>
    </View>
  );
}

interface GroupedEventRowProps {
  item: PositionedEventItem;
  groupItems: PositionedEventItem[];
  isSelected: boolean;
  isDraggingOut: boolean;
  onSelect: (seq: number | undefined) => void;
  onPress?: (event: CalendarEvent) => void;
  onMoveToMinutes: (event: CalendarEvent, minutes: number, dropCol?: number) => void;
  onDragStart?: (event: CalendarEvent, groupItems: PositionedEventItem[]) => void;
  onDragUpdate?: (seq: number, mins: number, hoverCol?: number) => void;
  onDragFinish?: () => void;
  onDragEdge?: (absoluteX: number, absoluteY: number, allowDaySwitch?: boolean) => void;
  onDragEdgeStop?: () => void;
  pointerYToMinutes: (absoluteY: number, grabOffsetY: number) => number;
  scrollOffsetUI: { value: number };
  scrollNativeGesture?: any;
  colLeft: number;
  colW: number;
  totalCols: number;
}

function GroupedEventRow({ item, groupItems, isSelected, isDraggingOut, onSelect, onPress, onMoveToMinutes, onDragStart, onDragUpdate, onDragFinish, onDragEdge, onDragEdgeStop, pointerYToMinutes, scrollOffsetUI, scrollNativeGesture, colLeft, colW, totalCols }: GroupedEventRowProps) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const isDragging = useSharedValue(0);
  const dragMoved = useSharedValue(0);
  const scrollAtDragStart = useSharedValue(0);
  const grabOffsetY = useSharedValue(0);
  const justDragged = useRef(false);
  const lastSnappedTime = useRef<number | null>(null);
  const lastSnappedCol = useRef<number | null>(null);
  const [dragLabel, setDragLabel] = useState<string | null>(null);
  const isDone = !!item.event.sent;
  const strikeStyle = isDone ? { textDecorationLine: 'line-through' as const } : undefined;
  const markDragged = useCallback(() => { justDragged.current = true; }, []);

  const startTime = DateTime.fromISO(item.event.runAtUtc).toLocal();
  const endTime = startTime.plus({ minutes: item.event.durationMinutes || EVENT_MIN_DURATION_MINUTES });

  const handlePress = useCallback(() => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    if (isSelected) {
      onPress?.(item.event);
    } else {
      onSelect(item.event.seq);
    }
  }, [isSelected, item.event, onPress, onSelect]);

  const getHoverCol = useCallback((tx: number) => {
    const rawX = colLeft + tx - EVENT_LEFT;
    const totalAvail = colW * Math.max(1, totalCols);
    const estimatedW = totalAvail / 2;
    return rawX > estimatedW * 0.5 ? 1 : 0;
  }, [colLeft, colW, totalCols]);

  const handleDragMove = useCallback((absoluteY: number, tx: number, grabOffset: number) => {
    const mins = pointerYToMinutes(absoluteY, grabOffset);
    const endMins = Math.min(1440, mins + (item.event.durationMinutes || EVENT_MIN_DURATION_MINUTES));
    const hoverCol = getHoverCol(tx);

    if (lastSnappedTime.current !== mins || lastSnappedCol.current !== hoverCol) {
      lastSnappedTime.current = mins;
      lastSnappedCol.current = hoverCol;
      setDragLabel(`${formatMinutesToAmPm(mins)} - ${formatMinutesToAmPm(endMins)}`);
      if (item.event.seq != null) onDragUpdate?.(item.event.seq, mins, hoverCol);
    }
  }, [getHoverCol, item.event.durationMinutes, item.event.seq, onDragUpdate, pointerYToMinutes]);

  const resetDragSnap = useCallback(() => {
    lastSnappedTime.current = null;
    lastSnappedCol.current = null;
  }, []);

  const finishDrag = useCallback((absoluteY: number, tx: number, grabOffset: number) => {
    const mins = pointerYToMinutes(absoluteY, grabOffset);
    const dropCol = getHoverCol(tx);
    resetDragSnap();
    setDragLabel(null);
    onMoveToMinutes(item.event, mins, dropCol);
    onDragFinish?.();
  }, [getHoverCol, item.event, onDragFinish, onMoveToMinutes, pointerYToMinutes, resetDragSnap]);

  const clearDrag = useCallback(() => {
    resetDragSnap();
    setDragLabel(null);
    onDragFinish?.();
  }, [onDragFinish, resetDragSnap]);

  const gesture = useMemo(() => {
    let pan = Gesture.Pan()
      .activateAfterLongPress(260)
      .failOffsetX([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .failOffsetY([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .onStart((e) => {
        isDragging.value = 1;
        dragMoved.value = 0;
        scrollAtDragStart.value = scrollOffsetUI.value;
        grabOffsetY.value = Math.max(0, Math.min(e.y, Math.max(EVENT_VISUAL_MIN_H, item.durationHeight)));
        runOnJS(markDragged)();
        runOnJS(Haptics.impactAsync)(Haptics.ImpactFeedbackStyle.Medium);
        if (onDragStart) runOnJS(onDragStart)(item.event, groupItems);
        runOnJS(handleDragMove)(e.absoluteY, e.translationX, grabOffsetY.value);
        if (onDragEdge) runOnJS(onDragEdge)(e.absoluteX, e.absoluteY, true);
      })
      .onUpdate((e) => {
        const scrollDelta = scrollOffsetUI.value - scrollAtDragStart.value;
        const effectiveY = e.translationY + scrollDelta;
        translateX.value = e.translationX;
        translateY.value = effectiveY;
        if (Math.abs(e.translationX) > 8 || Math.abs(e.translationY) > 8 || Math.abs(scrollDelta) > 2) {
          dragMoved.value = 1;
        }
        runOnJS(handleDragMove)(e.absoluteY, e.translationX, grabOffsetY.value);
        if (onDragEdge) runOnJS(onDragEdge)(e.absoluteX, e.absoluteY, true);
      })
      .onEnd((e) => {
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        if (dragMoved.value === 1) {
          runOnJS(finishDrag)(e.absoluteY, e.translationX, grabOffsetY.value);
        } else {
          runOnJS(clearDrag)();
        }
        translateX.value = withTiming(0, { duration: 150 });
        translateY.value = withTiming(0, { duration: 150 });
      })
      .onFinalize(() => {
        isDragging.value = 0;
        dragMoved.value = 0;
        translateX.value = withTiming(0, { duration: 150 });
        translateY.value = withTiming(0, { duration: 150 });
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        runOnJS(clearDrag)();
      });

    if (scrollNativeGesture) {
      pan = pan.blocksExternalGesture(scrollNativeGesture);
    }
    return pan;
  }, [clearDrag, dragMoved, finishDrag, grabOffsetY, groupItems, handleDragMove, isDragging, item.durationHeight, item.event, markDragged, onDragEdge, onDragEdgeStop, onDragStart, scrollAtDragStart, scrollNativeGesture, scrollOffsetUI, translateX, translateY]);

  const rowAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
    zIndex: isDragging.value ? 90 : 1,
  }));

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        // @ts-ignore - dataSet is a react-native-web extension for data-* attrs
        dataSet={{ eventBlock: 'true' }}
        style={[s.groupRow, isSelected ? s.groupRowSelected : null, isDraggingOut ? { opacity: 0 } : null, rowAnimatedStyle]}
      >
        <View style={[s.groupRowColor, { backgroundColor: item.color }]} />
        <Pressable onPress={handlePress} style={s.groupRowPressable}>
          <View style={s.groupRowMain}>
            <Text style={[s.groupRowTitle, { color: item.color }, strikeStyle]} numberOfLines={1}>{item.event.body}</Text>
            <Text style={s.groupRowTime} numberOfLines={1}>{startTime.toFormat('h:mm a')} - {endTime.toFormat('h:mm a')}</Text>
          </View>
          <Ionicons name="reorder-three" size={18} color={colors.textSecondary} />
        </Pressable>
        {dragLabel && (
          <View style={s.groupDragLabel} pointerEvents="none">
            <Text style={s.dragTimeLabelText}>{dragLabel}</Text>
          </View>
        )}
      </Animated.View>
    </GestureDetector>
  );
}

interface GroupedEventBlockProps {
  p: PositionedEventItem;
  colLeft: number;
  colW: number;
  isExpanded: boolean;
  selectedSeq: number | null;
  onToggle: (groupKey: string) => void;
  onSelect: (seq: number | undefined) => void;
  onPress?: (event: CalendarEvent) => void;
  onMoveToMinutes: (event: CalendarEvent, minutes: number, dropCol?: number) => void;
  onMoveGroupToMinutes: (items: PositionedEventItem[], minutes: number, dropCol?: number) => void;
  activeGroupItemDragSeq?: number | null;
  onDragStart?: (event: CalendarEvent) => void;
  onItemDragStart?: (event: CalendarEvent, groupItems: PositionedEventItem[]) => void;
  onDragUpdate?: (seq: number, mins: number, hoverCol?: number) => void;
  onGroupDragStart?: (items: PositionedEventItem[]) => void;
  onGroupDragUpdate?: (items: PositionedEventItem[], mins: number, hoverCol?: number) => void;
  onDragFinish?: () => void;
  onDragEdge?: (absoluteX: number, absoluteY: number, allowDaySwitch?: boolean) => void;
  onDragEdgeStop?: () => void;
  pointerYToMinutes: (absoluteY: number, grabOffsetY: number) => number;
  scrollOffsetUI: { value: number };
  scrollNativeGesture?: any;
  hourHeight: number;
  animatedHourHeight?: any;
  zoomContentOffsetUI?: SharedValue<number>;
}

function GroupedEventBlock({ p, colLeft, colW, isExpanded, selectedSeq, onToggle, onSelect, onPress, onMoveToMinutes, onMoveGroupToMinutes, activeGroupItemDragSeq, onDragStart, onItemDragStart, onDragUpdate, onGroupDragStart, onGroupDragUpdate, onDragFinish, onDragEdge, onDragEdgeStop, pointerYToMinutes, scrollOffsetUI, scrollNativeGesture, hourHeight, animatedHourHeight, zoomContentOffsetUI }: GroupedEventBlockProps) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const isDragging = useSharedValue(0);
  const dragMoved = useSharedValue(0);
  const frozenLeft = useSharedValue(colLeft);
  const frozenWidth = useSharedValue(colW);
  const animatedGroupLeft = useSharedValue(colLeft);
  const animatedGroupWidth = useSharedValue(colW);
  const scrollAtDragStart = useSharedValue(0);
  const justDragged = useRef(false);
  const lastGroupDragTime = useRef<number | null>(null);
  const lastGroupDragCol = useRef<number | null>(null);
  const [dragLabel, setDragLabel] = useState<string | null>(null);
  const groupEvents = p.groupEvents ?? [p];
  const isGroupItemDragSource = activeGroupItemDragSeq != null && groupEvents.some(item => item.event.seq === activeGroupItemDragSeq);
  const remainingGroupEvents = isGroupItemDragSource ? groupEvents.filter(item => item.event.seq !== activeGroupItemDragSeq) : groupEvents;
  const visibleGroupEvents = remainingGroupEvents.length > 0 ? remainingGroupEvents : groupEvents;
  const selectedInside = visibleGroupEvents.some(item => item.event.seq === selectedSeq);
  const isGroupActive = isExpanded || selectedInside;
  const groupKey = p.groupKey ?? `group-${p.originalMins}`;
  const visibleTitles = visibleGroupEvents.slice(0, 2).map(item => item.event.body).join(', ');
  const moreCount = Math.max(0, visibleGroupEvents.length - 2);
  const visibleColor = visibleGroupEvents[0]?.color ?? p.color;
  const blockHeight = Math.max(EVENT_VISUAL_MIN_H, p.height);
  const groupVisibleHeight = Math.min(blockHeight, Math.max(GROUP_COMPACT_MIN_H, getVisibleEventHeight(p.durationHeight, hourHeight, EVENT_GAP)));
  const originalGroupDurationHeight = Math.max(...groupEvents.map(item => item.durationHeight));
  const originalGroupBlockHeight = Math.max(EVENT_VISUAL_MIN_H, ...groupEvents.map(item => item.height));
  const originalGroupVisibleHeight = Math.min(originalGroupBlockHeight, Math.max(GROUP_COMPACT_MIN_H, getVisibleEventHeight(originalGroupDurationHeight, hourHeight, EVENT_GAP)));
  const isNarrowGroup = colW < 150;
  const isVeryNarrowGroup = colW < 118;
  const groupHorizontalPadding = isVeryNarrowGroup ? 5 : isNarrowGroup ? 6 : 8;
  const groupBadgeSize = isVeryNarrowGroup ? 16 : isNarrowGroup ? 18 : 20;
  const showGroupTime = colW >= 118;
  const popoverWidth = Math.max(colW, GROUP_POPOVER_MIN_WIDTH);
  const popoverLeft = colLeft + popoverWidth > EVENT_LEFT + EVENT_SIDE_INSET + popoverWidth ? Math.min(0, colW - popoverWidth) : 0;
  const maxDuration = Math.max(...groupEvents.map(item => item.event.durationMinutes || EVENT_MIN_DURATION_MINUTES));
  const shouldRenderPopover = isExpanded || isGroupItemDragSource;

  useEffect(() => {
    animatedGroupLeft.value = withTiming(colLeft, { duration: GROUP_LAYOUT_ANIM_MS });
    animatedGroupWidth.value = withTiming(colW, { duration: GROUP_LAYOUT_ANIM_MS });
  }, [animatedGroupLeft, animatedGroupWidth, colLeft, colW]);

  const markDragged = useCallback(() => { justDragged.current = true; }, []);

  const triggerHaptic = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  }, []);

  const handleToggle = useCallback(() => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    onToggle(groupKey);
  }, [groupKey, onToggle]);

  const getGroupHoverCol = useCallback((tx: number) => {
    const rawX = colLeft + tx - EVENT_LEFT;
    const totalAvail = colW * Math.max(1, p.totalCols || 1);
    const estimatedW = totalAvail / 2;
    return rawX > estimatedW * 0.5 ? 1 : 0;
  }, [colLeft, colW, p.totalCols]);

  const resetGroupDragSnap = useCallback(() => {
    lastGroupDragTime.current = null;
    lastGroupDragCol.current = null;
  }, []);

  const updateGroupDrag = useCallback((ty: number, tx: number) => {
    const newY = p.top + ty;
    const rawMins = ((newY - TOP_PAD) / hourHeight) * 60;
    const snapped = Math.round(Math.max(0, Math.min(1439, rawMins)) / 5) * 5;
    const hoverCol = getGroupHoverCol(tx);

    if (lastGroupDragTime.current !== snapped || lastGroupDragCol.current !== hoverCol) {
      lastGroupDragTime.current = snapped;
      lastGroupDragCol.current = hoverCol;
      setDragLabel(`${formatMinutesToAmPm(snapped)} - ${formatMinutesToAmPm(Math.min(1440, snapped + maxDuration))}`);
      onGroupDragUpdate?.(groupEvents, snapped, hoverCol);
    }
  }, [getGroupHoverCol, groupEvents, hourHeight, maxDuration, onGroupDragUpdate, p.top]);

  const clearDragLabel = useCallback(() => {
    resetGroupDragSnap();
    setDragLabel(null);
    onDragFinish?.();
  }, [onDragFinish, resetGroupDragSnap]);

  const handleGroupDragEnd = useCallback((ty: number, tx: number) => {
    const newY = p.top + ty;
    const rawMins = ((newY - TOP_PAD) / hourHeight) * 60;
    const snapped = Math.round(Math.max(0, Math.min(1439, rawMins)) / 5) * 5;
    const dropCol = getGroupHoverCol(tx);
    resetGroupDragSnap();
    setDragLabel(null);
    onMoveGroupToMinutes(groupEvents, snapped, dropCol);
    onDragFinish?.();
  }, [getGroupHoverCol, groupEvents, hourHeight, onDragFinish, onMoveGroupToMinutes, p.top, resetGroupDragSnap]);

  const gesture = useMemo(() => {
    let pan = Gesture.Pan()
      .activateAfterLongPress(260)
      .failOffsetX([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .failOffsetY([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .onStart(() => {
        isDragging.value = 1;
        dragMoved.value = 0;
        frozenLeft.value = colLeft;
        frozenWidth.value = colW;
        scrollAtDragStart.value = scrollOffsetUI.value;
        runOnJS(markDragged)();
        runOnJS(triggerHaptic)();
        if (onGroupDragStart) runOnJS(onGroupDragStart)(groupEvents);
        else if (onDragStart) runOnJS(onDragStart)(p.event);
        runOnJS(updateGroupDrag)(0, 0);
      })
      .onUpdate((e) => {
        const scrollDelta = scrollOffsetUI.value - scrollAtDragStart.value;
        const effectiveY = e.translationY + scrollDelta;
        translateX.value = e.translationX;
        translateY.value = effectiveY;
        if (Math.abs(e.translationX) > 8 || Math.abs(e.translationY) > 8 || Math.abs(scrollDelta) > 2) {
          dragMoved.value = 1;
        }
        runOnJS(updateGroupDrag)(effectiveY, e.translationX);
        if (onDragEdge) runOnJS(onDragEdge)(e.absoluteX, e.absoluteY, true);
      })
      .onEnd((e) => {
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        const scrollDelta = scrollOffsetUI.value - scrollAtDragStart.value;
        const effectiveY = e.translationY + scrollDelta;
        if (dragMoved.value === 1) {
          runOnJS(handleGroupDragEnd)(effectiveY, e.translationX);
        } else {
          runOnJS(clearDragLabel)();
        }
        translateX.value = withTiming(0, { duration: 150 });
        translateY.value = withTiming(0, { duration: 150 });
      })
      .onFinalize(() => {
        isDragging.value = 0;
        dragMoved.value = 0;
        translateX.value = withTiming(0, { duration: 150 });
        translateY.value = withTiming(0, { duration: 150 });
        if (onDragEdgeStop) runOnJS(onDragEdgeStop)();
        runOnJS(clearDragLabel)();
      });

    if (scrollNativeGesture) {
      pan = pan.blocksExternalGesture(scrollNativeGesture);
    }
    return pan;
  }, [clearDragLabel, colLeft, colW, dragMoved, frozenLeft, frozenWidth, groupEvents, handleGroupDragEnd, isDragging, markDragged, onDragEdge, onDragEdgeStop, onDragStart, onGroupDragStart, p.event, scrollAtDragStart, scrollNativeGesture, scrollOffsetUI, translateX, translateY, triggerHaptic, updateGroupDrag]);

  const animatedStyle = useAnimatedStyle(() => {
    const dragging = isDragging.value === 1;
    let topVal = p.top;
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const liveDurationHeight = (maxDuration / 60) * zoomH;
    const liveBlockHeight = Math.max(EVENT_VISUAL_MIN_H, liveDurationHeight);

    if (animatedHourHeight) {
      topVal = TOP_PAD + (p.originalMins / 60) * zoomH;
    }

    return {
      left: dragging ? frozenLeft.value : animatedGroupLeft.value,
      width: dragging ? frozenWidth.value : animatedGroupWidth.value,
      height: dragging ? blockHeight : liveBlockHeight,
      transform: [
        { translateX: translateX.value },
        {
          translateY: translateY.value
            + topVal - TOP_PAD
            + (zoomContentOffsetUI?.value ?? 0),
        },
      ],
      zIndex: dragging ? 95 : isExpanded ? 75 : selectedInside ? 50 : 2,
    };
  });

  const groupInnerAnimatedStyle = useAnimatedStyle(() => {
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const liveDurationHeight = (maxDuration / 60) * zoomH;
    const liveBlockHeight = Math.max(EVENT_VISUAL_MIN_H, liveDurationHeight);
    const liveVisibleHeight = Math.min(
      liveBlockHeight,
      Math.max(GROUP_COMPACT_MIN_H, liveDurationHeight - EVENT_GAP),
    );
    const progress = getScheduleTextProgress(liveVisibleHeight, GROUP_COMPACT_MIN_H, 62);
    return {
      height: liveVisibleHeight,
      minHeight: liveVisibleHeight,
      maxHeight: liveVisibleHeight,
      paddingVertical: 2 + progress * 3,
    };
  });

  const groupTitleTypographyStyle = useAnimatedStyle(() => {
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const liveHeight = Math.max(GROUP_COMPACT_MIN_H, (maxDuration / 60) * zoomH - EVENT_GAP);
    const progress = getScheduleTextProgress(liveHeight, GROUP_COMPACT_MIN_H, 62);
    const widthPenalty = isVeryNarrowGroup ? 1.5 : isNarrowGroup ? 0.5 : 0;
    return {
      fontSize: 9.5 + progress * 1.5 - widthPenalty,
      lineHeight: 11.5 + progress * 2.5 - widthPenalty * 0.5,
    };
  });

  const groupTimeTypographyStyle = useAnimatedStyle(() => {
    const zoomH = animatedHourHeight?.value ?? hourHeight;
    const liveHeight = Math.max(GROUP_COMPACT_MIN_H, (maxDuration / 60) * zoomH - EVENT_GAP);
    const progress = getScheduleTextProgress(liveHeight, 38, 58);
    return {
      fontSize: 8 + progress * 2,
      lineHeight: 10 + progress * 2,
      opacity: progress,
    };
  });

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        // @ts-ignore - dataSet is a react-native-web extension for data-* attrs
        dataSet={{ eventBlock: 'true' }}
        style={[
          s.eventBlock,
          isGroupActive ? s.eventBlockSelectedCompact : null,
          {
            top: TOP_PAD,
            borderLeftWidth: 0,
          },
          animatedStyle,
        ]}
      >
      <AnimatedPressable
        onPress={handleToggle}
        style={[
          s.groupBlockInner,
          {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: groupVisibleHeight,
            paddingHorizontal: groupHorizontalPadding,
            backgroundColor: isGroupActive ? '#FFFFFF' : getEventFillColor(visibleColor),
            borderLeftColor: visibleColor,
            borderColor: isGroupActive ? visibleColor : 'transparent',
            borderWidth: isGroupActive ? 2 : 0,
            borderLeftWidth: isGroupActive ? 4 : 3,
          },
          groupInnerAnimatedStyle,
        ]}
      >
        {isGroupActive && <View style={[StyleSheet.absoluteFill, { backgroundColor: visibleColor + '25', borderRadius: 8, borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }]} pointerEvents="none" />}
        <View style={[s.groupHeaderRow, { gap: isVeryNarrowGroup ? 4 : 6 }]}>
          <Animated.Text style={[s.groupTitle, { color: visibleColor }, groupTitleTypographyStyle]} numberOfLines={1}>{visibleTitles}{moreCount > 0 ? ` +${moreCount}` : ''}</Animated.Text>
          {visibleGroupEvents.length > 1 && (
            <View style={[s.groupCountBadge, { minWidth: groupBadgeSize, height: groupBadgeSize, borderRadius: groupBadgeSize / 2, paddingHorizontal: isVeryNarrowGroup ? 4 : 5, backgroundColor: visibleColor }]}>
              <Text style={s.groupCountText}>{visibleGroupEvents.length}</Text>
            </View>
          )}
        </View>
        {showGroupTime && (
          <Animated.Text
            style={[
              s.groupTimeText,
              { position: 'absolute', left: groupHorizontalPadding, bottom: 2 },
              groupTimeTypographyStyle,
            ]}
            numberOfLines={1}
          >
            {formatMinutesToAmPm(p.originalMins)}
          </Animated.Text>
        )}
      </AnimatedPressable>

      {shouldRenderPopover && (
        <View
          style={[
            s.groupPopover,
            {
              top: isGroupItemDragSource ? originalGroupVisibleHeight + 6 : groupVisibleHeight + 6,
              left: popoverLeft,
              width: popoverWidth,
              opacity: isGroupItemDragSource ? 0 : 1,
            },
          ]}
        >
          {groupEvents.map((item) => (
            <GroupedEventRow
              key={`group-row-${item.event.seq ?? item.event.body}`}
              item={item}
              groupItems={groupEvents}
              isSelected={selectedSeq === item.event.seq}
              isDraggingOut={activeGroupItemDragSeq != null && activeGroupItemDragSeq === item.event.seq}
              onSelect={onSelect}
              onPress={onPress}
              onMoveToMinutes={onMoveToMinutes}
              onDragStart={onItemDragStart}
              onDragUpdate={onDragUpdate}
              onDragFinish={onDragFinish}
              onDragEdge={onDragEdge}
              onDragEdgeStop={onDragEdgeStop}
              pointerYToMinutes={pointerYToMinutes}
              scrollOffsetUI={scrollOffsetUI}
              scrollNativeGesture={scrollNativeGesture}
              colLeft={colLeft}
              colW={colW}
              totalCols={p.totalCols || 1}
            />
          ))}
        </View>
      )}
      {dragLabel && (
        <View style={s.groupDragLabel} pointerEvents="none">
          <Text style={s.dragTimeLabelText}>{dragLabel}</Text>
        </View>
      )}
      </Animated.View>
    </GestureDetector>
  );
}

// ── Component ───────────────────────────────────────────────
export default function TimelineView({ events, allEvents, selectedDate, onPressEvent, onResizeEvent, onMoveEvent, onChangeColumn, onDragCreate, onNavigateDay, onSwipeNavigationPreview, tagColors, bottomPadding, zoomGestureActive }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const isWeb = Platform.OS === 'web';
  const scrollRef = useAnimatedRef<any>();
  const scrollNativeGesture = useMemo(() => Gesture.Native(), []);
  const scrollOffsetUI = useSharedValue(0);
  const viewportHeightUI = useSharedValue(0);
  const viewportWidthUI = useSharedValue(0);
  const pinchStartScrollY = useSharedValue(0);
  const pinchStartFocalY = useSharedValue(0);
  const pinchActive = useSharedValue(0);
  const pinchContentOffsetY = useSharedValue(0);
  const pinchScrollTargetY = useSharedValue(0);
  const animatedHourHeight = useSharedValue(DEFAULT_HOUR_HEIGHT);
  const daySlideX = useSharedValue(0);
  const daySlideOpacity = useSharedValue(1);
  const timelineSwipeLocked = useSharedValue(0);
  const swipePreviewDirectionUI = useSharedValue(0);
  const swipePreviewPreparingDirectionUI = useSharedValue(0);
  const swipePreviewOpacityUI = useSharedValue(0);
  const pendingSwipeTranslationXUI = useSharedValue(0);
  const renderedDateRef = useRef(selectedDate);
  const { width: screenWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(0);
  
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [dragInfo, setDragInfo] = useState<{ seq: number; mins: number; hoverCol?: number } | null>(null);
  const [activeDragEvent, setActiveDragEvent] = useState<CalendarEvent | null>(null);
  const [activeDragGroupEvents, setActiveDragGroupEvents] = useState<CalendarEvent[] | null>(null);
  const [activeGroupItemDragSeq, setActiveGroupItemDragSeq] = useState<number | null>(null);
  const [dragEdgeDirection, setDragEdgeDirection] = useState<-1 | 1 | null>(null);
  const [dragCreateScrollLocked, setDragCreateScrollLocked] = useState(false);
  const [localColPrefs, setLocalColPrefs] = useState<Record<number, number>>({});
  const [localTimeOverrides, setLocalTimeOverrides] = useState<Record<number, string>>({});
  const [layoutReady, setLayoutReady] = useState(false);

  const [previousSwipePreviewEvents, nextSwipePreviewEvents] = useMemo(() => {
    const selected = DateTime.fromISO(selectedDate);
    const previousDate = selected.minus({ days: 1 }).toISODate();
    const nextDate = selected.plus({ days: 1 }).toISODate();
    const previous: CalendarEvent[] = [];
    const next: CalendarEvent[] = [];

    (allEvents || events).forEach((sourceEvent) => {
      const event = sourceEvent.seq != null && localTimeOverrides[sourceEvent.seq]
        ? { ...sourceEvent, runAtUtc: localTimeOverrides[sourceEvent.seq] }
        : sourceEvent;
      if (!event.runAtUtc) return;
      const eventDate = DateTime.fromISO(event.runAtUtc).toLocal().toISODate();
      if (eventDate === previousDate) previous.push(event);
      else if (eventDate === nextDate) next.push(event);
    });

    return [previous, next];
  }, [allEvents, events, localTimeOverrides, selectedDate]);
  const [swipePreview, setSwipePreview] = useState<{
    direction: -1 | 1;
    events: CalendarEvent[];
  } | null>(() => ({
    direction: 1,
    events: nextSwipePreviewEvents,
  }));
  const swipePreviewDirection = swipePreview?.direction ?? null;
  const swipePreviewEvents = swipePreview?.events ?? [];
  const swipePreviewHandoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unlockAfterPreviewCommitRef = useRef(false);

  const clearEventSelection = useCallback(() => {
    setSelectedSeq(null);
    setExpandedGroupKey(null);
  }, []);

  const updateSwipePreview = useCallback((direction: -1 | 1 | null) => {
    if (swipePreviewHandoffTimer.current) {
      clearTimeout(swipePreviewHandoffTimer.current);
      swipePreviewHandoffTimer.current = null;
    }
    if (direction == null) {
      setSwipePreview(null);
      return;
    }
    setSwipePreview({
      direction,
      events: direction < 0 ? previousSwipePreviewEvents : nextSwipePreviewEvents,
    });
  }, [nextSwipePreviewEvents, previousSwipePreviewEvents]);

  useEffect(() => () => {
    if (swipePreviewHandoffTimer.current) clearTimeout(swipePreviewHandoffTimer.current);
  }, []);

  // Zoom state
  const [hourHeight, setHourHeight] = useState(DEFAULT_HOUR_HEIGHT);
  const hourHeightRef = useRef(DEFAULT_HOUR_HEIGHT);
  const baseZoomHeight = useSharedValue(DEFAULT_HOUR_HEIGHT);
  const lastZoomHeight = useSharedValue(DEFAULT_HOUR_HEIGHT);
  const scrollOffset = useRef(0);

  const timelineBottomPadding = Math.max(0, bottomPadding ?? TIMELINE_BOTTOM_PAD);
  const totalHeight = hourHeight * 24 + TOP_PAD + timelineBottomPadding;
  const availableWidth = containerWidth > 0 ? containerWidth : screenWidth;
  const visibleEventLaneWidth = Math.max(0, availableWidth - EVENT_LEFT - EVENT_RIGHT - EVENT_SIDE_INSET);

  // Auto-scroll during drag
  const viewportHeight = useRef(0);
  const viewportWidth = useRef(0);
  const scrollViewTopY = useRef(0);
  const scrollViewLeftX = useRef(0);
  const selectedDateRef = useRef(selectedDate);
  const autoScrollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const daySwitchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const daySwitchRepeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const dayTransitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const daySwitchDirection = useRef<-1 | 1 | 0>(0);
  const pendingDragTransitionDirection = useRef<-1 | 1 | null>(null);
  const pendingSwipeTransitionDirection = useRef<-1 | 1 | null>(null);
  const onNavigateDayRef = useRef(onNavigateDay);
  const activeDragEventRef = useRef<CalendarEvent | null>(null);
  const positionedRef = useRef<PositionedEventItem[]>([]);
  const dragEdgeDirectionRef = useRef<-1 | 1 | null>(null);
  const dragCreateScrollLockedRef = useRef(false);
  const autoScrollSpeed = useRef(0);
  const EDGE_ZONE = 60; // px from top/bottom edge to trigger auto-scroll
  const DAY_EDGE_ZONE = 46; // px from left/right edge to switch days while dragging
  const DAY_EDGE_SWITCH_HOLD_MS = 560;
  const DAY_EDGE_SWITCH_REPEAT_MS = 460;
  const SCROLL_STEP = 8; // px per tick

  useLayoutEffect(() => {
    if (!swipePreview) {
      swipePreviewOpacityUI.value = 0;
      swipePreviewDirectionUI.value = 0;
      swipePreviewPreparingDirectionUI.value = 0;
      pendingSwipeTranslationXUI.value = 0;
      return;
    }

    // A direction change can enqueue more than one React update while the
    // finger is still down. Only reveal the snapshot that matches the latest
    // requested direction; an older queued snapshot must never cross the edge.
    if (
      swipePreviewPreparingDirectionUI.value !== 0
      && swipePreviewPreparingDirectionUI.value !== swipePreview.direction
    ) return;

    const pageWidth = Math.max(1, viewportWidth.current || availableWidth);
    swipePreviewDirectionUI.value = swipePreview.direction;
    swipePreviewPreparingDirectionUI.value = swipePreview.direction;
    if (Math.abs(pendingSwipeTranslationXUI.value) > 0.5) {
      daySlideX.value = Math.max(
        -pageWidth,
        Math.min(pageWidth, pendingSwipeTranslationXUI.value),
      );
    }
    swipePreviewOpacityUI.value = 1;

    if (unlockAfterPreviewCommitRef.current) {
      unlockAfterPreviewCommitRef.current = false;
      timelineSwipeLocked.value = 0;
    }
  }, [
    availableWidth,
    daySlideX,
    pendingSwipeTranslationXUI,
    swipePreview,
    swipePreviewDirectionUI,
    swipePreviewOpacityUI,
    swipePreviewPreparingDirectionUI,
    timelineSwipeLocked,
  ]);

  useEffect(() => {
    if (
      pendingSwipeTransitionDirection.current != null
      || swipePreviewHandoffTimer.current
      || timelineSwipeLocked.value > 0
    ) return;

    // Keep one lightweight adjacent day ready while idle. This mirrors the
    // single-neighbor pager behavior without paying to mount both directions.
    setSwipePreview((current) => {
      const direction = current?.direction ?? 1;
      const previewEvents = direction < 0
        ? previousSwipePreviewEvents
        : nextSwipePreviewEvents;
      if (current?.direction === direction && current.events === previewEvents) return current;
      return { direction, events: previewEvents };
    });
  }, [
    nextSwipePreviewEvents,
    previousSwipePreviewEvents,
    timelineSwipeLocked,
  ]);

  useEffect(() => {
    onNavigateDayRef.current = onNavigateDay;
  }, [onNavigateDay]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    dragCreateScrollLockedRef.current = dragCreateScrollLocked;
  }, [dragCreateScrollLocked]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollTimer.current) {
      clearInterval(autoScrollTimer.current);
      autoScrollTimer.current = null;
    }
    autoScrollSpeed.current = 0;
  }, []);

  const stopDaySwitch = useCallback(() => {
    if (daySwitchTimer.current) {
      clearTimeout(daySwitchTimer.current);
      daySwitchTimer.current = null;
    }
    if (daySwitchRepeatTimer.current) {
      clearInterval(daySwitchRepeatTimer.current);
      daySwitchRepeatTimer.current = null;
    }
    if (dayTransitionTimer.current) {
      clearTimeout(dayTransitionTimer.current);
      dayTransitionTimer.current = null;
    }
    daySwitchDirection.current = 0;
  }, []);

  const updateDragEdgeDirection = useCallback((direction: -1 | 1 | null) => {
    if (dragEdgeDirectionRef.current === direction) return;
    dragEdgeDirectionRef.current = direction;
    setDragEdgeDirection(direction);
    if (direction == null) {
      daySlideX.value = withTiming(0, { duration: 150 });
      daySlideOpacity.value = withTiming(1, { duration: 120 });
      return;
    }
    const edgePeek = Math.min(30, Math.max(18, viewportWidth.current * 0.08));
    daySlideX.value = withTiming(-direction * edgePeek, { duration: 180 });
    daySlideOpacity.value = withTiming(0.975, { duration: 180 });
  }, [daySlideOpacity, daySlideX]);

  const commitDayNavigation = useCallback((direction: -1 | 1) => {
    if (!onNavigateDayRef.current) return;
    const nextSelectedDate = DateTime.fromISO(selectedDateRef.current).plus({ days: direction }).toISODate();
    if (nextSelectedDate) selectedDateRef.current = nextSelectedDate;
    onNavigateDayRef.current?.(direction);
  }, []);

  const navigateDayOnce = useCallback((direction: -1 | 1) => {
    if (!onNavigateDayRef.current) return;
    commitDayNavigation(direction);
  }, [commitDayNavigation]);

  const cancelPendingSwipeTransition = useCallback(() => {
    pendingSwipeTransitionDirection.current = null;
  }, []);

  const previewSwipeNavigation = useCallback((direction: -1 | 1 | null) => {
    onSwipeNavigationPreview?.(direction);
  }, [onSwipeNavigationPreview]);

  useLayoutEffect(() => {
    const previousDate = renderedDateRef.current;
    if (previousDate === selectedDate) return;
    renderedDateRef.current = selectedDate;
    const dragDirection = pendingDragTransitionDirection.current;
    if (dragDirection != null && activeDragEventRef.current) {
      pendingDragTransitionDirection.current = null;
      const entryDistance = Math.min(86, Math.max(48, viewportWidth.current * 0.22));
      const edgePeek = Math.min(30, Math.max(18, viewportWidth.current * 0.08));
      daySlideX.value = dragDirection * entryDistance;
      daySlideOpacity.value = 0.94;
      daySlideX.value = withTiming(-dragDirection * edgePeek, { duration: 190 });
      daySlideOpacity.value = withTiming(0.975, { duration: 170 });
      return;
    }
    const swipeDirection = pendingSwipeTransitionDirection.current;
    if (swipeDirection != null) {
      pendingSwipeTransitionDirection.current = null;
      daySlideX.value = 0;
      daySlideOpacity.value = 1;
      swipePreviewDirectionUI.value = 0;
      swipePreviewPreparingDirectionUI.value = 0;
      pendingSwipeTranslationXUI.value = 0;
      // Keep the painted destination over the newly committed timeline for a
      // few frames. Then replace it atomically, off-screen, with the next day
      // in the same direction before accepting another swipe.
      swipePreviewHandoffTimer.current = setTimeout(() => {
        swipePreviewHandoffTimer.current = null;
        // The landed preview is still at x=0 here. Hide it before changing its
        // React content so Fabric cannot paint the following day's reminders
        // for a frame while the layer is being returned off-screen.
        swipePreviewOpacityUI.value = 0;
        unlockAfterPreviewCommitRef.current = true;
        setSwipePreview({
          direction: swipeDirection,
          events: swipeDirection < 0
            ? previousSwipePreviewEvents
            : nextSwipePreviewEvents,
        });
      }, 56);
      return;
    }
    // The gesture already communicated direction. The new day only needs one
    // quiet settle to the resting position; there is no second entry slide.
    daySlideX.value = withTiming(0, { duration: 145 });
    daySlideOpacity.value = 1;
    timelineSwipeLocked.value = 0;
    const externalDirection: -1 | 1 = selectedDate < previousDate ? -1 : 1;
    setSwipePreview({
      direction: externalDirection,
      events: externalDirection < 0
        ? previousSwipePreviewEvents
        : nextSwipePreviewEvents,
    });
  }, [
    daySlideOpacity,
    daySlideX,
    pendingSwipeTranslationXUI,
    nextSwipePreviewEvents,
    previousSwipePreviewEvents,
    selectedDate,
    swipePreviewDirectionUI,
    swipePreviewOpacityUI,
    swipePreviewPreparingDirectionUI,
    timelineSwipeLocked,
  ]);

  const switchDayWhileDragging = useCallback((direction: -1 | 1) => {
    if (dayTransitionTimer.current) return;
    const exitDistance = Math.min(96, Math.max(56, viewportWidth.current * 0.24));
    daySlideX.value = withTiming(-direction * exitDistance, { duration: 110 });
    daySlideOpacity.value = withTiming(0.92, { duration: 100 });
    dayTransitionTimer.current = setTimeout(() => {
      dayTransitionTimer.current = null;
      if (daySwitchDirection.current !== direction) {
        daySlideX.value = withTiming(0, { duration: 130 });
        daySlideOpacity.value = withTiming(1, { duration: 110 });
        return;
      }
      pendingDragTransitionDirection.current = direction;
      navigateDayOnce(direction);
      Haptics.selectionAsync().catch(() => undefined);
    }, 105);
  }, [daySlideOpacity, daySlideX, navigateDayOnce]);

  const startDaySwitch = useCallback((direction: -1 | 1) => {
    if (!onNavigateDayRef.current) return;
    if (
      daySwitchDirection.current === direction
      && (daySwitchTimer.current || daySwitchRepeatTimer.current)
    ) return;

    stopDaySwitch();
    daySwitchDirection.current = direction;
    daySwitchTimer.current = setTimeout(() => {
      daySwitchTimer.current = null;
      if (daySwitchDirection.current !== direction) return;
      switchDayWhileDragging(direction);
      daySwitchRepeatTimer.current = setInterval(() => {
        if (daySwitchDirection.current !== direction) {
          if (daySwitchRepeatTimer.current) {
            clearInterval(daySwitchRepeatTimer.current);
            daySwitchRepeatTimer.current = null;
          }
          return;
        }
        switchDayWhileDragging(direction);
      }, DAY_EDGE_SWITCH_REPEAT_MS);
    }, DAY_EDGE_SWITCH_HOLD_MS);
  }, [stopDaySwitch, switchDayWhileDragging]);

  const stopInteractionEdges = useCallback(() => {
    stopDaySwitch();
    stopAutoScroll();
    updateDragEdgeDirection(null);
  }, [stopAutoScroll, stopDaySwitch, updateDragEdgeDirection]);

  const handleTimelineSwipeEnd = useCallback((translationX: number, translationY: number, velocityX = 0) => {
    // Nested native gestures can deliver duplicate end callbacks on Android.
    // Treat a page change as one transaction until the destination is mounted.
    if (timelineSwipeLocked.value > 0 || pendingSwipeTransitionDirection.current != null) return;
    const settleCurrentPage = () => {
      daySlideX.value = withSpring(
        0,
        { damping: 22, stiffness: 260, mass: 0.75 },
        (finished) => {
          if (finished) {
            const preparedDirection = swipePreviewDirectionUI.value
              || swipePreviewPreparingDirectionUI.value;
            swipePreviewDirectionUI.value = preparedDirection;
            swipePreviewPreparingDirectionUI.value = preparedDirection;
            swipePreviewOpacityUI.value = preparedDirection === 0 ? 0 : 1;
            pendingSwipeTranslationXUI.value = 0;
          }
        },
      );
      daySlideOpacity.value = 1;
    };
    if (
      !onNavigateDayRef.current
      || activeDragEventRef.current
      || dragCreateScrollLockedRef.current
    ) {
      settleCurrentPage();
      return;
    }
    const absX = Math.abs(translationX);
    const absY = Math.abs(translationY);
    if (absX < absY * TIMELINE_SWIPE_AXIS_RATIO) {
      settleCurrentPage();
      return;
    }
    const hasDistance = absX >= TIMELINE_SWIPE_COMMIT_PX;
    const hasVelocity = absX >= TIMELINE_SWIPE_ACTIVATE_PX && Math.abs(velocityX) >= TIMELINE_SWIPE_MIN_VELOCITY;
    if (!hasDistance && !hasVelocity) {
      settleCurrentPage();
      return;
    }

    const direction: -1 | 1 = translationX < 0 ? 1 : -1;
    const pageWidth = Math.max(1, viewportWidth.current || availableWidth);
    pendingSwipeTransitionDirection.current = direction;
    timelineSwipeLocked.value = 1;
    // The header can optimistically show the accepted destination while the
    // pre-rendered timeline page completes its native settle. The real date is
    // still committed at the end, keeping reminder content frozen and smooth.
    previewSwipeNavigation(direction);
    daySlideOpacity.value = 1;
    daySlideX.value = withTiming(
      -direction * pageWidth,
      { duration: TIMELINE_PAGE_EXIT_MS },
      (finished) => {
        if (finished) {
          runOnJS(commitDayNavigation)(direction);
        } else {
          timelineSwipeLocked.value = 0;
          runOnJS(cancelPendingSwipeTransition)();
          runOnJS(previewSwipeNavigation)(null);
        }
      },
    );
  }, [
    availableWidth,
    cancelPendingSwipeTransition,
    commitDayNavigation,
    daySlideOpacity,
    daySlideX,
    pendingSwipeTranslationXUI,
    previewSwipeNavigation,
    swipePreviewDirectionUI,
    swipePreviewOpacityUI,
    swipePreviewPreparingDirectionUI,
    timelineSwipeLocked,
    updateSwipePreview,
  ]);

  const handleDragEdge = useCallback((absoluteX: number, absoluteY: number, allowDaySwitch = false) => {
    const relativeY = absoluteY - scrollViewTopY.current;
    const relativeX = absoluteX - scrollViewLeftX.current;
    const vh = viewportHeight.current;
    const vw = viewportWidth.current;
    if (vh <= 0) return;

    let speed = 0;
    if (relativeY < EDGE_ZONE) {
      // Near top — scroll up
      speed = -SCROLL_STEP * Math.max(0.3, 1 - relativeY / EDGE_ZONE);
    } else if (relativeY > vh - EDGE_ZONE) {
      // Near bottom — scroll down
      speed = SCROLL_STEP * Math.max(0.3, 1 - (vh - relativeY) / EDGE_ZONE);
    }

    autoScrollSpeed.current = speed;

    if (speed !== 0 && !autoScrollTimer.current) {
      autoScrollTimer.current = setInterval(() => {
        const next = Math.max(0, Math.min(totalHeight - vh, scrollOffset.current + autoScrollSpeed.current));
        scrollOffset.current = next;
        scrollOffsetUI.value = next;
        scrollRef.current?.scrollTo({ y: next, animated: false });
      }, 16);
    } else if (speed === 0 && autoScrollTimer.current) {
      clearInterval(autoScrollTimer.current);
      autoScrollTimer.current = null;
    }
    if (vw > 0 && allowDaySwitch) {
      if (relativeX < DAY_EDGE_ZONE) {
        updateDragEdgeDirection(-1);
        startDaySwitch(-1);
      } else if (relativeX > vw - DAY_EDGE_ZONE) {
        updateDragEdgeDirection(1);
        startDaySwitch(1);
      } else {
        updateDragEdgeDirection(null);
        stopDaySwitch();
      }
    }
  }, [startDaySwitch, stopDaySwitch, totalHeight, updateDragEdgeDirection]);

  // Clean up timer on unmount
  useEffect(() => () => {
    stopAutoScroll();
    stopDaySwitch();
  }, [stopAutoScroll, stopDaySwitch]);

  const scrollContainerRef = useRef<View>(null);

  const measureScrollViewFrame = useCallback(() => {
    scrollContainerRef.current?.measureInWindow?.((x: number, y: number, width: number, height: number) => {
      scrollViewLeftX.current = x;
      scrollViewTopY.current = y;
      if (width > 0) viewportWidth.current = width;
      if (height > 0) viewportHeight.current = height;
    });
  }, []);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width: w, height: h } = event.nativeEvent.layout;
    viewportHeight.current = h;
    viewportHeightUI.value = h;
    viewportWidth.current = w;
    viewportWidthUI.value = w;
    setLayoutReady(true);
    setContainerWidth((current) => (Math.abs(current - w) > 1 ? w : current));
    requestAnimationFrame(() => measureScrollViewFrame());
  }, [measureScrollViewFrame, viewportHeightUI, viewportWidthUI]);

  const syncScrollOffset = useCallback((y: number) => {
    scrollOffset.current = y;
  }, []);

  const handleZoomEnd = useCallback((newH: number, finalScrollY?: number) => {
    hourHeightRef.current = newH;
    if (typeof finalScrollY === 'number') {
      scrollOffset.current = finalScrollY;
    }
    setHourHeight(newH);
  }, []);

  const handleScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      const maxScroll = Math.max(
        0,
        TOP_PAD + 24 * animatedHourHeight.value + timelineBottomPadding - viewportHeightUI.value,
      );
      const nextY = Math.min(event.contentOffset.y, maxScroll);
      if (pinchActive.value > 0) {
        const lockedY = Math.min(pinchScrollTargetY.value, maxScroll);
        // Native ScrollView also receives the touch stream. Keep it locked to
        // the UI-thread zoom target so centroid drift and pointer release can
        // never introduce a second, competing vertical movement.
        if (Math.abs(event.contentOffset.y - lockedY) > 0.5) {
          scrollTo(scrollRef, 0, lockedY, false);
        }
        scrollOffsetUI.value = lockedY;
        return;
      }
      scrollOffsetUI.value = nextY;
      if (event.contentOffset.y > maxScroll + 0.5) {
        scrollTo(scrollRef, 0, maxScroll, false);
      }
    },
    onEndDrag: (event) => {
      runOnJS(syncScrollOffset)(pinchActive.value > 0 ? pinchScrollTargetY.value : event.contentOffset.y);
    },
    onMomentumEnd: (event) => {
      runOnJS(syncScrollOffset)(pinchActive.value > 0 ? pinchScrollTargetY.value : event.contentOffset.y);
    },
  });

    const pinchGesture = useMemo(() =>
      Gesture.Pinch()
        .simultaneousWithExternalGesture(scrollNativeGesture)
        .onStart((e) => {
          cancelAnimation(pinchActive);
          pinchActive.value = 1;
          if (zoomGestureActive) {
            cancelAnimation(zoomGestureActive);
            zoomGestureActive.value = 1;
          }
          baseZoomHeight.value = lastZoomHeight.value;
          pinchStartScrollY.value = scrollOffsetUI.value;
          pinchScrollTargetY.value = scrollOffsetUI.value;
          pinchStartFocalY.value = e.focalY;
          pinchContentOffsetY.value = 0;
        })
        .onUpdate((e) => {
          if (e.numberOfPointers < 2) return;
          const newH = clampHourHeight(baseZoomHeight.value * e.scale);
          if (Math.abs(newH - lastZoomHeight.value) > 0.1) {
            lastZoomHeight.value = newH;
            animatedHourHeight.value = newH;
            // Keep the schedule point under the initial two-finger midpoint.
            // Android emits a terminal update whose focalY jumps to the last
            // remaining pointer; never use that moving focal as an anchor.
            const targetScrollY = getAnchoredScheduleScroll(
              pinchStartScrollY.value,
              pinchStartFocalY.value,
              pinchStartFocalY.value,
              baseZoomHeight.value,
              newH,
              viewportHeightUI.value,
              TOP_PAD,
              timelineBottomPadding,
            );
            pinchScrollTargetY.value = targetScrollY;
            scrollOffsetUI.value = targetScrollY;
            scrollTo(scrollRef, 0, targetScrollY, false);
          }
        })
        .onEnd(() => {
          const finalH = lastZoomHeight.value;
          const finalScrollY = pinchScrollTargetY.value;
          scrollOffsetUI.value = finalScrollY;
          scrollTo(scrollRef, 0, finalScrollY, false);
          runOnJS(handleZoomEnd)(finalH, finalScrollY);
        })
        .onFinalize((_event, success) => {
          if (!success) {
            animatedHourHeight.value = baseZoomHeight.value;
            lastZoomHeight.value = baseZoomHeight.value;
            pinchScrollTargetY.value = pinchStartScrollY.value;
            scrollOffsetUI.value = pinchStartScrollY.value;
            scrollTo(scrollRef, 0, pinchStartScrollY.value, false);
            pinchContentOffsetY.value = 0;
          }
          pinchActive.value = withDelay(120, withTiming(0, { duration: 0 }));
          if (zoomGestureActive) {
            zoomGestureActive.value = withDelay(120, withTiming(0, { duration: 0 }));
          }
        }),
    [handleZoomEnd, pinchActive, pinchContentOffsetY, pinchScrollTargetY, pinchStartFocalY, scrollNativeGesture, timelineBottomPadding, viewportHeightUI, zoomGestureActive]);

  // Sync incoming api colPrefs with local overrides
  const colPrefs = useMemo(() => {
    const combined: Record<number, number> = { ...localColPrefs };
    events.forEach(e => {
      if (e.seq != null && e.colPref != null && localColPrefs[e.seq] == null) {
        combined[e.seq] = e.colPref;
      }
    });
    return combined;
  }, [events, localColPrefs]);

  const displayEvents = useMemo(() => {
    return events.map((event) => {
      if (event.seq == null) return event;
      const localRunAt = localTimeOverrides[event.seq];
      return localRunAt ? { ...event, runAtUtc: localRunAt } : event;
    });
  }, [events, localTimeOverrides]);

  useEffect(() => {
    setLocalTimeOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const event of events) {
        if (event.seq != null && next[event.seq] && event.runAtUtc === next[event.seq]) {
          delete next[event.seq];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [events]);

  const handleDragUpdate = useCallback((seq: number, mins: number, hoverCol?: number) => {
    setDragInfo({ seq, mins, hoverCol });
  }, []);

  const pointerYToSnappedMinutes = useCallback((absoluteY: number, grabOffsetY: number) => {
    const contentY = absoluteY - scrollViewTopY.current + scrollOffset.current - grabOffsetY;
    const rawMins = ((contentY - TOP_PAD) / hourHeightRef.current) * 60;
    return Math.round(Math.max(0, Math.min(1439, rawMins)) / 5) * 5;
  }, []);

  const handleGroupItemDragUpdate = useCallback((seq: number, mins: number, hoverCol?: number) => {
    setActiveGroupItemDragSeq(seq);
    setDragInfo({ seq, mins, hoverCol });
  }, []);

  const handleEventDragStart = useCallback((event: CalendarEvent) => {
    activeDragEventRef.current = event;
    setActiveDragEvent(event);
    setActiveDragGroupEvents(null);
    setActiveGroupItemDragSeq(null);
  }, []);

  const handleGroupItemDragStart = useCallback((event: CalendarEvent, groupItems: PositionedEventItem[]) => {
    const eventsToKeepMounted = groupItems.map(item => item.event);
    activeDragEventRef.current = event;
    setActiveDragEvent(event);
    setActiveDragGroupEvents(eventsToKeepMounted.length ? eventsToKeepMounted : null);
    setActiveGroupItemDragSeq(null);
  }, []);

  const handleGroupDragStart = useCallback((items: PositionedEventItem[]) => {
    const eventsToDrag = items.map(item => item.event);
    const representative = eventsToDrag[0] ?? null;
    activeDragEventRef.current = representative;
    setActiveDragEvent(representative);
    setActiveDragGroupEvents(eventsToDrag);
    setActiveGroupItemDragSeq(null);
    setExpandedGroupKey(null);
  }, []);

  const handleGroupDragUpdate = useCallback((items: PositionedEventItem[], mins: number, hoverCol?: number) => {
    const representativeSeq = items[0]?.event.seq;
    if (representativeSeq == null) return;
    setDragInfo({ seq: representativeSeq, mins, hoverCol });
  }, []);

  const commitDragColumnLayout = useCallback((info: { seq: number; mins: number; hoverCol?: number }, dropCol: number, extraDropSeqs: number[] = []) => {
    const batch: Array<{seq: number; colPref: number}> = [];
    const prefUpdates: Record<number, number> = {};
    const committedSeqs = new Set<number>([info.seq, ...extraDropSeqs]);

    const pushPref = (seq: number, colPref: number) => {
      prefUpdates[seq] = colPref;
      batch.push({ seq, colPref });
    };

    for (const p of positionedRef.current) {
      const seq = p.event.seq;
      if (seq == null || committedSeqs.has(seq)) continue;
      if (p.dragCol !== p.origCol) {
        pushPref(seq, p.dragCol);
      }
    }

    committedSeqs.forEach(seq => pushPref(seq, dropCol));

    setLocalColPrefs(prev => ({ ...prev, ...prefUpdates }));
    if (onChangeColumn && batch.length > 0) {
      onChangeColumn(batch);
    }
  }, [onChangeColumn]);

  const handleDragClear = useCallback((dropCol?: number) => {
    stopInteractionEdges();
    activeDragEventRef.current = null;
    setActiveDragEvent(null);
    setActiveDragGroupEvents(null);
    setActiveGroupItemDragSeq(null);
    setDragInfo(info => {
      if (info && dropCol != null) {
        const h = Math.floor(info.mins / 60);
        const m = info.mins % 60;
        const localRunAt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO();
        if (localRunAt) {
          setLocalTimeOverrides(prev => ({ ...prev, [info.seq]: localRunAt }));
        }

        commitDragColumnLayout(info, dropCol);
      }
      return null;
    });
  }, [commitDragColumnLayout, stopInteractionEdges]);

  const handleEventMove = useCallback((event: CalendarEvent, mins: number) => {
    if (event.seq == null || !onMoveEvent) return;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const localRunAt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO();
    if (!localRunAt) return;
    setLocalTimeOverrides(prev => ({ ...prev, [event.seq as number]: localRunAt }));
    onMoveEvent(event.seq, localRunAt);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }, [onMoveEvent]);

  const handleGroupItemMove = useCallback((event: CalendarEvent, mins: number, dropCol?: number) => {
    if (event.seq == null || !onMoveEvent) return;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const localRunAt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO();
    if (!localRunAt) return;
    if (dropCol != null) {
      commitDragColumnLayout({ seq: event.seq, mins, hoverCol: dropCol }, dropCol);
    }
    setLocalTimeOverrides(prev => ({ ...prev, [event.seq as number]: localRunAt }));
    setExpandedGroupKey(null);
    setActiveGroupItemDragSeq(null);
    onMoveEvent(event.seq, localRunAt);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }, [commitDragColumnLayout, onMoveEvent]);

  const handleGroupBatchMove = useCallback((items: PositionedEventItem[], mins: number, dropCol?: number) => {
    if (!onMoveEvent) return;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const localRunAt = DateTime.fromISO(selectedDateRef.current).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO();
    if (!localRunAt) return;

    if (dropCol != null) {
      const representativeSeq = items[0]?.event.seq;
      const extraDropSeqs = items.slice(1).map(item => item.event.seq).filter((seq): seq is number => seq != null);
      if (representativeSeq != null) {
        commitDragColumnLayout({ seq: representativeSeq, mins, hoverCol: dropCol }, dropCol, extraDropSeqs);
      }
    }

    const overrides: Record<number, string> = {};
    items.forEach((item) => {
      const seq = item.event.seq;
      if (seq == null) return;
      overrides[seq] = localRunAt;
      onMoveEvent(seq, localRunAt);
    });

    if (Object.keys(overrides).length > 0) {
      setLocalTimeOverrides(prev => ({ ...prev, ...overrides }));
    }
    setExpandedGroupKey(null);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }, [commitDragColumnLayout, onMoveEvent]);

  const finishGroupItemDrag = useCallback(() => {
    stopInteractionEdges();
    activeDragEventRef.current = null;
    setActiveDragEvent(null);
    setActiveDragGroupEvents(null);
    setActiveGroupItemDragSeq(null);
    setDragInfo(null);
  }, [stopInteractionEdges]);

  // Deselect when date changes
  useEffect(() => {
    if (activeDragEvent || activeDragGroupEvents?.length) return;
    setSelectedSeq(null);
    setDragInfo(null);
    setExpandedGroupKey(null);
  }, [activeDragEvent, activeDragGroupEvents, selectedDate]);

  const isToday = useMemo(() => {
    return DateTime.local().toISODate() === selectedDate;
  }, [selectedDate]);

  const initialScrollOffset = useRef<number | null>(null);
  if (initialScrollOffset.current === null) {
    const eventMinutes = displayEvents
      .filter((event) => event.runAtUtc)
      .map((event) => minutesOfDay(event.runAtUtc));
    const targetMinutes = isToday
      ? DateTime.local().hour * 60 + DateTime.local().minute
      : eventMinutes.length > 0 ? Math.min(...eventMinutes) : 8 * 60;
    initialScrollOffset.current = Math.max(
      0,
      TOP_PAD + (targetMinutes / 60) * hourHeight - INITIAL_TOP_VIEWPORT_GAP,
    );
  }

  // Auto-scroll logic — snap to nearest event on non-today, or now-line on today.
  // Keep the initial focus useful, then preserve the user's exact viewport across day changes.
  const eventsRef = useRef(displayEvents);
  eventsRef.current = displayEvents;
  const didInitialTimelineFocus = useRef(false);

  const performScroll = useCallback(() => {
    if (!layoutReady || activeDragEventRef.current || dragCreateScrollLockedRef.current) return;
    let targetMin: number;
    if (isToday) {
      targetMin = DateTime.local().hour * 60 + DateTime.local().minute;
    } else {
      // Snap to earliest event, fallback to 8 AM
      const eventMinutes = eventsRef.current
        .filter((e) => e.runAtUtc)
        .map((e) => minutesOfDay(e.runAtUtc));
      targetMin = eventMinutes.length > 0 ? Math.min(...eventMinutes) : 8 * 60;
    }
    const y = Math.max(0, TOP_PAD + (targetMin / 60) * hourHeight - INITIAL_TOP_VIEWPORT_GAP);
    scrollRef.current?.scrollTo({ y, animated: false });
  }, [isToday, layoutReady, hourHeight]);

  // Focus once when Timeline first opens. Horizontal day navigation must not chase reminders.
  useEffect(() => {
    if (didInitialTimelineFocus.current || !layoutReady) return;
    didInitialTimelineFocus.current = true;
    performScroll();
  }, [layoutReady, performScroll]);

  // Position events and assign columns for overlapping items

  const timelineEvents = useMemo(() => {
    const activeEvents = activeDragGroupEvents?.length ? activeDragGroupEvents : activeDragEvent ? [activeDragEvent] : [];
    if (activeEvents.length === 0) return displayEvents;

    const displaySeqs = new Set(displayEvents.map(event => event.seq).filter(seq => seq != null));
    const additions = activeEvents.flatMap((event) => {
      if (!event.runAtUtc || event.seq == null || displaySeqs.has(event.seq)) return [];
      const originalLocal = DateTime.fromISO(event.runAtUtc).toLocal();
      const activeLocal = DateTime.fromISO(selectedDate).set({
        hour: originalLocal.hour,
        minute: originalLocal.minute,
        second: 0,
        millisecond: 0,
      });
      const activeRunAtUtc = activeLocal.toUTC().toISO() || event.runAtUtc;
      return [{ ...event, runAtUtc: activeRunAtUtc }];
    });

    return additions.length > 0 ? [...displayEvents, ...additions] : displayEvents;
  }, [activeDragEvent, activeDragGroupEvents, displayEvents, selectedDate]);
  
  const positioned = useMemo(() => {
    const sourceItems: PositionedEventItem[] = timelineEvents.filter((e) => !!e.runAtUtc).map(e => {
      const originalMins = minutesOfDay(e.runAtUtc);
      const top = TOP_PAD + (originalMins / 60) * hourHeight;
      const dur = e.durationMinutes ?? 0;
      const durationHeight = dur > 0 ? (dur / 60) * hourHeight : hourHeight / 2;
      const height = Math.max(EVENT_VISUAL_MIN_H, durationHeight);
      const originalEndMins = dur > 0 ? originalMins + dur : originalMins + EVENT_OVERLAP_WINDOW;
      return { 
        event: e, 
        top, 
        height, 
        durationHeight,
        originalMins, 
        originalEndMins,
        layoutEndMins: getStableLayoutEndMins(originalMins, originalEndMins),
        color: getEventColor(e, tagColors),
        origCol: 0, origTotalCols: 1,
        dragCol: 0, dragTotalCols: 1,
        col: 0, totalCols: 1,
      };
    });

    const separatedDragSeqs = new Set<number>();
    if (activeGroupItemDragSeq == null) {
      const activeGroupSeqs = (activeDragGroupEvents ?? [])
        .map(event => event.seq)
        .filter((seq): seq is number => seq != null);
      if (activeGroupSeqs.length > 0) {
        activeGroupSeqs.forEach(seq => separatedDragSeqs.add(seq));
      } else if (activeDragEvent?.seq != null) {
        separatedDragSeqs.add(activeDragEvent.seq);
      }
    }

    const passiveSourceItems: PositionedEventItem[] = [];
    const activeDragSourceItems: PositionedEventItem[] = [];
    sourceItems.forEach(item => {
      const seq = item.event.seq;
      if (seq != null && separatedDragSeqs.has(seq)) activeDragSourceItems.push(item);
      else passiveSourceItems.push(item);
    });

    const buildSameStartItems = (items: PositionedEventItem[], groupKeyPrefix: string) => {
      const sameStartGroups = new Map<number, PositionedEventItem[]>();
      items.forEach(item => {
        const group = sameStartGroups.get(item.originalMins) ?? [];
        group.push(item);
        sameStartGroups.set(item.originalMins, group);
      });

      return Array.from(sameStartGroups.values()).map((group) => {
        if (group.length === 1) return group[0];

        const sortedGroup = [...group].sort((a, b) => {
          const durA = a.event.durationMinutes ?? 0;
          const durB = b.event.durationMinutes ?? 0;
          if (durA !== durB) return durB - durA;
          return (a.event.seq ?? 0) - (b.event.seq ?? 0);
        });
        const visibleSortedGroup = activeGroupItemDragSeq != null
          ? sortedGroup.filter(item => item.event.seq !== activeGroupItemDragSeq)
          : sortedGroup;
        const layoutGroup = visibleSortedGroup.length > 0 ? visibleSortedGroup : sortedGroup;
        const representative = layoutGroup[0];
        const originalEndMins = Math.max(...layoutGroup.map(item => item.originalEndMins));
        const durationHeight = Math.max(...layoutGroup.map(item => item.durationHeight));
        const height = Math.max(EVENT_VISUAL_MIN_H, durationHeight);
        const layoutEndMins = getStableLayoutEndMins(representative.originalMins, originalEndMins);

        return {
          ...representative,
          height,
          durationHeight,
          originalEndMins,
          layoutEndMins,
          isGroup: true,
          groupKey: `${groupKeyPrefix}-${representative.originalMins}`,
          groupEvents: sortedGroup,
        };
      });
    };

    let baseItems: PositionedEventItem[] = [
      ...buildSameStartItems(passiveSourceItems, 'start'),
      ...buildSameStartItems(activeDragSourceItems, 'drag-start'),
    ];

    if (activeGroupItemDragSeq != null && activeDragEvent?.seq === activeGroupItemDragSeq && dragInfo?.seq === activeGroupItemDragSeq) {
      const mins = dragInfo.mins;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const runAtUtc = DateTime.fromISO(selectedDate).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO() || activeDragEvent.runAtUtc;
      const cloneEvent = { ...activeDragEvent, runAtUtc };
      const dur = cloneEvent.durationMinutes ?? 0;
      const durationHeight = dur > 0 ? (dur / 60) * hourHeight : hourHeight / 2;
      const top = TOP_PAD + (mins / 60) * hourHeight;
      const originalEndMins = dur > 0 ? mins + dur : mins + EVENT_OVERLAP_WINDOW;

      baseItems = [
        ...baseItems,
        {
          event: cloneEvent,
          top,
          height: Math.max(EVENT_VISUAL_MIN_H, durationHeight),
          durationHeight,
          originalMins: mins,
          originalEndMins,
          layoutEndMins: getStableLayoutEndMins(mins, originalEndMins),
          color: getEventColor(cloneEvent, tagColors),
          origCol: 0,
          origTotalCols: 1,
          dragCol: 0,
          dragTotalCols: 1,
          col: 0,
          totalCols: 1,
          isDragClone: true,
        },
      ];
    }

    // --- 1. Compute Original Stable Packing ---
    const sortedOrig = [...baseItems].sort((a, b) => a.originalMins - b.originalMins);
    let origGroups: (typeof sortedOrig)[] = [];
    let origCurrGroup: typeof sortedOrig = [];
    for (const item of sortedOrig) {
      if (origCurrGroup.length === 0 || item.originalMins < origCurrGroup.reduce((mx, g) => Math.max(mx, g.layoutEndMins), 0)) {
        origCurrGroup.push(item);
      } else {
        origGroups.push(origCurrGroup);
        origCurrGroup = [item];
      }
    }
    if (origCurrGroup.length > 0) origGroups.push(origCurrGroup);

    for (const group of origGroups) {
      const sortedGroup = [...group].sort((a, b) => {
        if (a.originalMins !== b.originalMins) return a.originalMins - b.originalMins;

         const hasPrefA = a.event.seq && colPrefs[a.event.seq] != null ? 1 : 0;
         const hasPrefB = b.event.seq && colPrefs[b.event.seq] != null ? 1 : 0;
         if (hasPrefA !== hasPrefB) return hasPrefB - hasPrefA;

         const prefA = (a.event.seq && colPrefs[a.event.seq] != null) ? colPrefs[a.event.seq] : 0;
         const prefB = (b.event.seq && colPrefs[b.event.seq] != null) ? colPrefs[b.event.seq] : 0;
         if (prefA !== prefB) return prefA - prefB;
        return (a.event.seq ?? 0) - (b.event.seq ?? 0);
      });
      let columns: (typeof sortedOrig)[] = [];
      for (const item of sortedGroup) {
        let placed = false;
        const preferredCol = (item.event.seq && colPrefs[item.event.seq] != null) ? colPrefs[item.event.seq] : 0;

        const startCol = Math.min(preferredCol, columns.length);
        while (columns.length <= startCol) {
          columns.push([]);
        }

        for (let i = startCol; i < columns.length; i++) {
          if (!columns[i].some(c => item.originalMins < c.layoutEndMins && item.layoutEndMins > c.originalMins)) {
            columns[i].push(item); item.origCol = i; placed = true; break;
          }
        }
        if (!placed) {
           for(let i=0; i < startCol; i++) {
              if (!columns[i].some(c => item.originalMins < c.layoutEndMins && item.layoutEndMins > c.originalMins)) {
                columns[i].push(item); item.origCol = i; placed = true; break;
              }
           }
        }
        if (!placed) {
          item.origCol = columns.length; columns.push([item]);
        }
      }
      group.forEach(item => { item.origTotalCols = columns.length; });
    }

    // --- 2. Compute Simulated Drag Packing for Dynamic Shifting ---
    const dragSimulatedItems = baseItems.map(item => {
      let m = item.originalMins;
      let eEnd = item.originalEndMins;
      let layoutEnd = item.layoutEndMins;
      const dur = item.event.durationMinutes ?? 0;
      let virtualColPref = (item.event.seq && colPrefs[item.event.seq] != null) ? colPrefs[item.event.seq] : 0;
      const isDraggedSingle = !!dragInfo && item.event.seq === dragInfo.seq && (!item.isGroup || item.isDragClone);
      const isDraggedGroup = !!dragInfo && activeGroupItemDragSeq == null && item.isGroup && item.event.seq === dragInfo.seq;
      
      if (dragInfo && (isDraggedSingle || isDraggedGroup)) {
        m = dragInfo.mins;
        const draggedDuration = isDraggedGroup
          ? Math.max(EVENT_MIN_DURATION_MINUTES, item.originalEndMins - item.originalMins)
          : dur > 0 ? dur : EVENT_OVERLAP_WINDOW;
        eEnd = m + draggedDuration;
        const layoutDuration = Math.max(draggedDuration, item.layoutEndMins - item.originalMins);
        layoutEnd = Math.min(1440, m + layoutDuration);
        if (dragInfo.hoverCol != null) virtualColPref = dragInfo.hoverCol;
      }
      return { ...item, simMins: m, simEndMins: eEnd, simLayoutEndMins: layoutEnd, pref: virtualColPref, sourceItem: item };
    }).sort((a, b) => a.simMins - b.simMins);

    let simGroups: (typeof dragSimulatedItems)[] = [];
    let simCurrGroup: typeof dragSimulatedItems = [];
    for (const item of dragSimulatedItems) {
      if (simCurrGroup.length === 0 || item.simMins < simCurrGroup.reduce((mx, g) => Math.max(mx, g.simLayoutEndMins), 0)) {
        simCurrGroup.push(item);
      } else {
        simGroups.push(simCurrGroup);
        simCurrGroup = [item];
      }
    }
    if (simCurrGroup.length > 0) simGroups.push(simCurrGroup);

    for (const group of simGroups) {
      const sortedGroup = [...group].sort((a, b) => {
         if (a.simMins !== b.simMins) return a.simMins - b.simMins;

         const aIsDragged = dragInfo && a.event.seq === dragInfo.seq && ((!a.isGroup || a.isDragClone) || (activeGroupItemDragSeq == null && a.isGroup)) ? 1 : 0;
         const bIsDragged = dragInfo && b.event.seq === dragInfo.seq && ((!b.isGroup || b.isDragClone) || (activeGroupItemDragSeq == null && b.isGroup)) ? 1 : 0;
         if (aIsDragged !== bIsDragged) return bIsDragged - aIsDragged;

         const hasPrefA = a.event.seq && colPrefs[a.event.seq] != null ? 1 : 0;
         const hasPrefB = b.event.seq && colPrefs[b.event.seq] != null ? 1 : 0;
         if (hasPrefA !== hasPrefB) return hasPrefB - hasPrefA;

         if (a.pref !== b.pref) return a.pref - b.pref;
        return (a.event.seq ?? 0) - (b.event.seq ?? 0);
      });
      let columns: (typeof dragSimulatedItems)[] = [];
      for (const item of sortedGroup) {
        let placed = false;
        const preferredCol = item.pref != null ? item.pref : 0;

        const startCol = Math.min(preferredCol, columns.length);
        while (columns.length <= startCol) {
          columns.push([]);
        }

        for (let i = startCol; i < columns.length; i++) {
          if (!columns[i].some(c => item.simMins < c.simLayoutEndMins && item.simLayoutEndMins > c.simMins)) {
            columns[i].push(item); item.dragCol = i; placed = true; break;
          }
        }
        if (!placed) {
           for(let i=0; i < startCol; i++) {
              if (!columns[i].some(c => item.simMins < c.simLayoutEndMins && item.simLayoutEndMins > c.simMins)) {
                columns[i].push(item); item.dragCol = i; placed = true; break;
              }
           }
        }
        if (!placed) {
          item.dragCol = columns.length; columns.push([item]);
        }
      }
      group.forEach(simItem => {
         simItem.sourceItem.dragCol = simItem.dragCol;
         simItem.sourceItem.dragTotalCols = columns.length;
      });
    }

    // Set map variables for EventBlockProps compatibility
    return baseItems.map(b => ({
      ...b,
      mins: b.originalMins,
      endMins: b.originalEndMins,
      col: b.dragCol,
      totalCols: b.dragTotalCols
    }));

  }, [timelineEvents, tagColors, dragInfo, colPrefs, hourHeight, totalHeight, activeDragEvent, activeDragGroupEvents, activeGroupItemDragSeq, selectedDate]);

  // Keep a ref so drop commits can read the latest simulated column layout
  positionedRef.current = positioned;

  useEffect(() => {
    if (!expandedGroupKey) return;
    const stillGrouped = positioned.some(item => item.isGroup && item.groupKey === expandedGroupKey);
    if (!stillGrouped) setExpandedGroupKey(null);
  }, [expandedGroupKey, positioned]);

  const maxOverlapColumns = useMemo(
    () => positioned.reduce((max, item) => Math.max(max, item.totalCols || 1), 1),
    [positioned]
  );
  const widestOverlapLaneWidth = maxOverlapColumns * EVENT_MIN_COLUMN_WIDTH + Math.max(0, maxOverlapColumns - 1) * EVENT_GAP;
  const timelineContentWidth = Math.ceil(Math.max(
    availableWidth,
    EVENT_LEFT + EVENT_RIGHT + EVENT_SIDE_INSET * 2 + widestOverlapLaneWidth
  ));
  const hasHorizontalOverflow = timelineContentWidth > availableWidth + 1;

  const timelineBodySizeStyle = useAnimatedStyle(() => ({
    // Keep the native ScrollView extent stable. Its usable range is clamped
    // by handleScroll, so zoom never invalidates content size mid-gesture.
    height: TOP_PAD + 24 * MAX_HOUR_HEIGHT + timelineBottomPadding,
  }));
  const currentTimelinePageStyle = useAnimatedStyle(() => ({
    opacity: daySlideOpacity.value,
    transform: [{ translateX: daySlideX.value }],
  }));
  const swipePreviewPageStyle = useAnimatedStyle(() => ({
    opacity: swipePreviewOpacityUI.value,
    transform: [
      {
        translateX: daySlideX.value
          + swipePreviewDirectionUI.value * Math.max(1, viewportWidthUI.value),
      },
    ],
  }));
  const swipePreviewContentOffsetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: -scrollOffsetUI.value }],
  }));
  const hourGridOffsetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: isWeb ? pinchContentOffsetY.value : 0 }],
  }));
  const eventLayerOffsetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: 0 }],
  }));

  // ── Drag-to-create on empty timeline slots ────────────────
  const dragCreateStartMins = useSharedValue(-1);
  const dragCreateEndMins = useSharedValue(-1);
  const dragCreateTouchY = useSharedValue(0);
  const dragCreateScrollStart = useSharedValue(0);
  const dragCreateActive = useSharedValue(false);
  const [dragCreateLabel, setDragCreateLabel] = useState<string | null>(null);

  const finishDragCreateInteraction = useCallback(() => {
    setDragCreateScrollLocked(false);
    stopInteractionEdges();
  }, [stopInteractionEdges]);

  const updateDragCreateLabel = useCallback((startM: number, endM: number) => {
    const s = Math.min(startM, endM);
    const e = Math.max(startM, endM);
    const dur = Math.max(15, e - s);
    setDragCreateLabel(`${formatMinutesToAmPm(s)} – ${formatMinutesToAmPm(s + dur)}`);
  }, []);

  const triggerDragCreate = useCallback((startM: number, dur: number) => {
    setDragCreateLabel(null);
    onDragCreate?.(startM, dur);
  }, [onDragCreate]);

  const clearDragCreate = useCallback(() => {
    setDragCreateLabel(null);
  }, []);

  const dragCreateGhostStyle = useAnimatedStyle(() => {
    if (!dragCreateActive.value) return { opacity: 0, height: 0, top: 0 };
    const s = Math.min(dragCreateStartMins.value, dragCreateEndMins.value);
    const e = Math.max(dragCreateStartMins.value, dragCreateEndMins.value);
    const dur = Math.max(15, e - s);
    const geometryHourHeight = animatedHourHeight.value;
    return {
      opacity: 1,
      top: TOP_PAD + (s / 60) * geometryHourHeight,
      height: (dur / 60) * geometryHourHeight,
    };
  });

  const dragCreateGesture = useMemo(() => {
    return Gesture.Pan()
      .activateAfterLongPress(360)
      .failOffsetX([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .failOffsetY([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
      .blocksExternalGesture(scrollNativeGesture)
      .enabled(!!onDragCreate)
      .onStart((e) => {
        dragCreateTouchY.value = e.y;
        dragCreateScrollStart.value = scrollOffsetUI.value;
        const rawMins = ((e.y - TOP_PAD) / animatedHourHeight.value) * 60;
        const snapped = Math.round(Math.max(0, Math.min(1425, rawMins)) / 15) * 15;
        dragCreateStartMins.value = snapped;
        dragCreateEndMins.value = snapped + 15;
        dragCreateActive.value = true;
        runOnJS(setDragCreateScrollLocked)(true);
        runOnJS(Haptics.impactAsync)(Haptics.ImpactFeedbackStyle.Medium);
        runOnJS(updateDragCreateLabel)(snapped, snapped + 15);
        runOnJS(handleDragEdge)(e.absoluteX, e.absoluteY);
      })
      .onUpdate((e) => {
        const scrollDelta = scrollOffsetUI.value - dragCreateScrollStart.value;
        const currentY = dragCreateTouchY.value + e.translationY + scrollDelta;
        const rawMins = ((currentY - TOP_PAD) / animatedHourHeight.value) * 60;
        const snapped = Math.round(Math.max(0, Math.min(1440, rawMins)) / 15) * 15;
        dragCreateEndMins.value = snapped;
        runOnJS(updateDragCreateLabel)(dragCreateStartMins.value, snapped);
        runOnJS(handleDragEdge)(e.absoluteX, e.absoluteY);
      })
      .onEnd(() => {
        const s = Math.min(dragCreateStartMins.value, dragCreateEndMins.value);
        const e = Math.max(dragCreateStartMins.value, dragCreateEndMins.value);
        const dur = Math.max(15, e - s);
        dragCreateActive.value = false;
        runOnJS(finishDragCreateInteraction)();
        runOnJS(triggerDragCreate)(s, dur);
      })
      .onFinalize((_, success) => {
        if (!success) {
          dragCreateActive.value = false;
          runOnJS(finishDragCreateInteraction)();
          runOnJS(clearDragCreate)();
        }
      });
  }, [onDragCreate, scrollNativeGesture, scrollOffsetUI, updateDragCreateLabel, handleDragEdge, finishDragCreateInteraction, triggerDragCreate, clearDragCreate]);

  const timelineSwipeGesture = useMemo(() => {
    return Gesture.Pan()
      .activeOffsetX([-TIMELINE_SWIPE_ACTIVATE_PX, TIMELINE_SWIPE_ACTIVATE_PX])
      .failOffsetY([-TIMELINE_SWIPE_MAX_VERTICAL_PX, TIMELINE_SWIPE_MAX_VERTICAL_PX])
      .maxPointers(1)
      .simultaneousWithExternalGesture(scrollNativeGesture)
      .enabled(!!onNavigateDay && !hasHorizontalOverflow)
      .onUpdate((e) => {
        if (dragCreateActive.value || pinchActive.value > 0 || timelineSwipeLocked.value > 0) return;
        if (Math.abs(e.translationX) < TIMELINE_SWIPE_ACTIVATE_PX) return;
        const pageWidth = Math.max(1, viewportWidthUI.value);
        const previewDirection: -1 | 1 = e.translationX < 0 ? 1 : -1;
        pendingSwipeTranslationXUI.value = Math.max(
          -pageWidth,
          Math.min(pageWidth, e.translationX),
        );
        if (swipePreviewDirectionUI.value !== previewDirection) {
          // Never move an already-painted page in as the next date. Rapid
          // swipes can begin during the short native-view handoff, so keep the
          // committed page still until React has installed the right snapshot.
          if (swipePreviewPreparingDirectionUI.value !== previewDirection) {
            swipePreviewPreparingDirectionUI.value = previewDirection;
            swipePreviewOpacityUI.value = 0;
            daySlideX.value = 0;
            runOnJS(updateSwipePreview)(previewDirection);
          }
          return;
        }
        if (swipePreviewPreparingDirectionUI.value !== previewDirection) {
          swipePreviewPreparingDirectionUI.value = previewDirection;
          runOnJS(updateSwipePreview)(previewDirection);
        }
        swipePreviewOpacityUI.value = 1;
        daySlideX.value = pendingSwipeTranslationXUI.value;
        daySlideOpacity.value = 1;
      })
      .onEnd((e) => {
        if (dragCreateActive.value || pinchActive.value > 0) return;
        runOnJS(handleTimelineSwipeEnd)(e.translationX, e.translationY, e.velocityX);
      })
      .onFinalize((_event, success) => {
        if (!success) {
          daySlideX.value = withSpring(
            0,
            { damping: 22, stiffness: 260, mass: 0.75 },
            (finished) => {
              if (finished) {
                const preparedDirection = swipePreviewDirectionUI.value
                  || swipePreviewPreparingDirectionUI.value;
                swipePreviewDirectionUI.value = preparedDirection;
                swipePreviewPreparingDirectionUI.value = preparedDirection;
                swipePreviewOpacityUI.value = preparedDirection === 0 ? 0 : 1;
                pendingSwipeTranslationXUI.value = 0;
              }
            },
          );
          daySlideOpacity.value = 1;
        }
      });
  }, [
    daySlideOpacity,
    daySlideX,
    dragCreateActive,
    handleTimelineSwipeEnd,
    hasHorizontalOverflow,
    onNavigateDay,
    pendingSwipeTranslationXUI,
    pinchActive,
    scrollNativeGesture,
    swipePreviewDirectionUI,
    swipePreviewOpacityUI,
    swipePreviewPreparingDirectionUI,
    timelineSwipeLocked,
    updateSwipePreview,
    viewportWidthUI,
  ]);

  const nativeTimelineGesture = useMemo(() => {
    return Gesture.Simultaneous(pinchGesture, timelineSwipeGesture);
  }, [pinchGesture, timelineSwipeGesture]);

  // ── Web: Ctrl+Wheel zoom & touchpad pinch ────────────────
  const webContainerRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = (webContainerRef.current as any);
    // react-native-web exposes the underlying DOM node
    const el: HTMLElement | null = node && (node instanceof HTMLElement ? node : node._nativeTag ?? node.getNode?.() ?? null);
    if (!el) return;

    let timelineScrollElement: HTMLElement | null = null;

    const getTimelineScrollElement = () => {
      if (timelineScrollElement && document.contains(timelineScrollElement)) return timelineScrollElement;
      timelineScrollElement = Array.from(document.querySelectorAll('*')).find((child) => {
        const htmlChild = child as HTMLElement;
        const text = htmlChild.textContent || '';
        return htmlChild.scrollHeight > htmlChild.clientHeight + 10
          && getComputedStyle(htmlChild).overflowY !== 'visible'
          && text.includes('12 AM')
          && text.includes('11 PM');
      }) as HTMLElement | null;
      return timelineScrollElement;
    };

    const scrollWebTimelineTo = (y: number, nextHourHeight = hourHeightRef.current) => {
      const maxY = Math.max(0, TOP_PAD + 24 * nextHourHeight + timelineBottomPadding - viewportHeight.current);
      const nextY = Math.max(0, Math.min(maxY, y));
      scrollOffset.current = nextY;
      scrollOffsetUI.value = nextY;
      scrollRef.current?.scrollTo?.({ y: nextY, animated: false });
      const scrollElement = getTimelineScrollElement();
      if (scrollElement) scrollElement.scrollTop = nextY;
    };

    const getCurrentWebTimelineScroll = () => {
      const scrollElement = getTimelineScrollElement();
      const current = scrollElement ? scrollElement.scrollTop : scrollOffset.current;
      scrollOffset.current = current;
      scrollOffsetUI.value = current;
      return current;
    };

    const getWebTimelineViewportTop = () => {
      const scrollElement = getTimelineScrollElement();
      if (scrollElement) return scrollElement.getBoundingClientRect().top;
      return scrollViewTopY.current || el.getBoundingClientRect().top;
    };

    const settleWebTimelineScroll = (y: number, nextHourHeight = hourHeightRef.current) => {
      requestAnimationFrame(() => {
        scrollWebTimelineTo(y, nextHourHeight);
        requestAnimationFrame(() => scrollWebTimelineTo(y, nextHourHeight));
      });
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+wheel or touchpad pinch → zoom
        e.preventDefault();
        e.stopPropagation();
        const delta = -e.deltaY;
        const zoomSpeed = 0.5;
        const prev = hourHeightRef.current;
        const newH = Math.round(
          Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, prev + delta * zoomSpeed))
        );
        if (newH !== prev) {
          // Keep focal point stable under cursor
          const cursorY = e.clientY - getWebTimelineViewportTop();
          const currentScroll = getCurrentWebTimelineScroll();
          const focalTime = (currentScroll + cursorY - TOP_PAD) / prev;
          const newScrollY = Math.max(0, TOP_PAD + focalTime * newH - cursorY);

          hourHeightRef.current = newH;
          animatedHourHeight.value = newH;
          lastZoomHeight.value = newH;
          setHourHeight(newH);
          scrollWebTimelineTo(newScrollY, newH);
          settleWebTimelineScroll(newScrollY, newH);
        }
      }
      // else: regular wheel scroll — let it pass through to ScrollView
    };

    // ── Blank-space reservation + grab-to-scroll ──
    let grabActive = false;
    let grabStartY = 0;
    let grabStartScroll = 0;
    let blankPressTimer: ReturnType<typeof setTimeout> | null = null;
    let blankPressStartX = 0;
    let blankPressStartY = 0;
    let blankPressActive = false;
    let blankPressPointerId: number | null = null;
    let blankPressTouchId: number | null = null;
    let touchSwipeStartX = 0;
    let touchSwipeStartY = 0;
    let touchSwipeLastX = 0;
    let touchSwipeLastY = 0;
    let touchSwipeActive = false;
    let touchPinchActive = false;
    let pinchTouchIds: [number, number] | null = null;
    let pinchStartDistance = 0;
    let pinchStartHeight = hourHeightRef.current;
    let pinchFocalMinutes = 0;
    let pinchLastHeight = hourHeightRef.current;
    let pinchLastScrollY = scrollOffset.current;
    let pinchFrame: number | null = null;
    let pendingPinchZoom: { height: number; scrollY: number } | null = null;
    const BLANK_LONG_PRESS_MS = 520;
    const BLANK_MOVE_CANCEL_PX = 12;
    const originalTouchAction = el.style.touchAction;
    const originalUserSelect = el.style.userSelect;

    const lockBrowserTouch = () => {
      el.style.touchAction = 'none';
      el.style.userSelect = 'none';
      (el.style as any).webkitUserSelect = 'none';
      (el.style as any).webkitTouchCallout = 'none';
    };

    const unlockBrowserTouch = () => {
      el.style.touchAction = originalTouchAction;
      el.style.userSelect = originalUserSelect;
      (el.style as any).webkitUserSelect = originalUserSelect;
      (el.style as any).webkitTouchCallout = '';
    };

    const clearBlankPress = () => {
      if (blankPressTimer) {
        clearTimeout(blankPressTimer);
        blankPressTimer = null;
      }
    };

    const getTouchDistance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const getTouchMidpoint = (a: Touch, b: Touch) => ({
      clientX: (a.clientX + b.clientX) / 2,
      clientY: (a.clientY + b.clientY) / 2,
    });

    const findPinchTouches = (touches: TouchList): [Touch, Touch] | null => {
      if (touches.length < 2) return null;
      if (!pinchTouchIds) return [touches[0], touches[1]];
      let first: Touch | null = null;
      let second: Touch | null = null;
      for (let i = 0; i < touches.length; i += 1) {
        if (touches[i].identifier === pinchTouchIds[0]) first = touches[i];
        if (touches[i].identifier === pinchTouchIds[1]) second = touches[i];
      }
      return first && second ? [first, second] : null;
    };

    const applyPinchZoom = (height: number, scrollY: number) => {
      pinchLastHeight = height;
      pinchLastScrollY = scrollY;
      pendingPinchZoom = { height, scrollY };
      hourHeightRef.current = height;
      animatedHourHeight.value = height;
      lastZoomHeight.value = height;
      scrollOffsetUI.value = scrollY;

      if (pinchFrame != null) return;
      pinchFrame = requestAnimationFrame(() => {
        pinchFrame = null;
        if (!pendingPinchZoom) return;
        const pending = pendingPinchZoom;
        pendingPinchZoom = null;
        setHourHeight(pending.height);
        scrollWebTimelineTo(pending.scrollY, pending.height);
        settleWebTimelineScroll(pending.scrollY, pending.height);
      });
    };

    const finishPinchZoom = () => {
      if (pinchFrame != null) {
        cancelAnimationFrame(pinchFrame);
        pinchFrame = null;
      }
      const finalHeight = pendingPinchZoom?.height ?? pinchLastHeight;
      const finalScrollY = pendingPinchZoom?.scrollY ?? pinchLastScrollY;
      pendingPinchZoom = null;
      hourHeightRef.current = finalHeight;
      animatedHourHeight.value = finalHeight;
      lastZoomHeight.value = finalHeight;
      setHourHeight(finalHeight);
      scrollWebTimelineTo(finalScrollY, finalHeight);
      settleWebTimelineScroll(finalScrollY, finalHeight);
      touchPinchActive = false;
      pinchTouchIds = null;
      pinchStartDistance = 0;
      unlockBrowserTouch();
    };

    const beginPinchZoom = (touches: TouchList) => {
      const first = touches[0];
      const second = touches[1];
      const distance = getTouchDistance(first, second);
      if (distance <= 0) return false;

      clearBlankPress();
      cancelBlankReservation();
      grabActive = false;
      touchSwipeActive = false;
      blankPressTouchId = null;
      pinchTouchIds = [first.identifier, second.identifier];
      pinchStartDistance = distance;
      pinchStartHeight = hourHeightRef.current;
      const midpoint = getTouchMidpoint(first, second);
      const viewportY = midpoint.clientY - getWebTimelineViewportTop();
      pinchLastScrollY = getCurrentWebTimelineScroll();
      pinchFocalMinutes = (pinchLastScrollY + viewportY - TOP_PAD) / pinchStartHeight;
      touchPinchActive = true;
      lockBrowserTouch();
      return true;
    };

    const getBlankPressMinutes = (clientY: number, maxMinutes = 1440) => {
      const contentY = clientY - getWebTimelineViewportTop() + getCurrentWebTimelineScroll();
      const rawMins = ((contentY - TOP_PAD) / hourHeightRef.current) * 60;
      return Math.round(Math.max(0, Math.min(maxMinutes, rawMins)) / 15) * 15;
    };

    const beginBlankReservation = (clientX: number, clientY: number) => {
      const snapped = getBlankPressMinutes(clientY, 1425);
      dragCreateStartMins.value = snapped;
      dragCreateEndMins.value = snapped + 15;
      dragCreateActive.value = true;
      blankPressActive = true;
      grabActive = false;
      el.style.cursor = 'crosshair';
      lockBrowserTouch();
      setDragCreateScrollLocked(true);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      updateDragCreateLabel(snapped, snapped + 15);
      handleDragEdge(clientX, clientY);
    };

    const updateBlankReservation = (clientX: number, clientY: number) => {
      if (!blankPressActive) return;
      const snapped = getBlankPressMinutes(clientY);
      dragCreateEndMins.value = snapped;
      updateDragCreateLabel(dragCreateStartMins.value, snapped);
      handleDragEdge(clientX, clientY);
    };

    const commitBlankReservation = () => {
      if (!blankPressActive) return;
      const s = Math.min(dragCreateStartMins.value, dragCreateEndMins.value);
      const e = Math.max(dragCreateStartMins.value, dragCreateEndMins.value);
      const dur = Math.max(15, e - s);
      dragCreateActive.value = false;
      blankPressActive = false;
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      unlockBrowserTouch();
      triggerDragCreate(s, dur);
    };

    const cancelBlankReservation = () => {
      if (!blankPressActive) return;
      dragCreateActive.value = false;
      blankPressActive = false;
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      unlockBrowserTouch();
      clearDragCreate();
    };

    const beginBlankPress = (clientX: number, clientY: number) => {
      if (!onDragCreate) return;
      clearBlankPress();
      blankPressStartX = clientX;
      blankPressStartY = clientY;
      blankPressTimer = setTimeout(() => {
        blankPressTimer = null;
        beginBlankReservation(clientX, clientY);
      }, BLANK_LONG_PRESS_MS);
    };

    const updateBlankPress = (clientX: number, clientY: number) => {
      if (!blankPressTimer) return;
      const dx = Math.abs(clientX - blankPressStartX);
      const dy = Math.abs(clientY - blankPressStartY);
      if (dx > BLANK_MOVE_CANCEL_PX || dy > BLANK_MOVE_CANCEL_PX) {
        clearBlankPress();
      }
    };

    const findTouch = (touches: TouchList, identifier: number | null) => {
      if (identifier == null) return null;
      for (let i = 0; i < touches.length; i += 1) {
        if (touches[i].identifier === identifier) return touches[i];
      }
      return null;
    };

    const resetTouchSwipe = () => {
      touchSwipeActive = false;
      touchSwipeStartX = 0;
      touchSwipeStartY = 0;
      touchSwipeLastX = 0;
      touchSwipeLastY = 0;
    };

    const isScrollbarPointer = (target: HTMLElement | null, clientX: number, clientY: number) => {
      const scrollbarHitSize = 18;
      const relativeX = clientX - scrollViewLeftX.current;
      const relativeY = clientY - scrollViewTopY.current;
      if (
        hasHorizontalOverflow &&
        viewportWidth.current > 0 &&
        viewportHeight.current > 0 &&
        relativeX >= 0 &&
        relativeX <= viewportWidth.current &&
        relativeY >= viewportHeight.current - scrollbarHitSize &&
        relativeY <= viewportHeight.current + scrollbarHitSize
      ) {
        return true;
      }

      let current: HTMLElement | null = target;
      while (current && el.contains(current)) {
        const style = getComputedStyle(current);
        const rect = current.getBoundingClientRect();
        const overflowX = style.overflowX;
        const overflowY = style.overflowY;
        const canScrollX = current.scrollWidth > current.clientWidth + 1 && (overflowX === 'auto' || overflowX === 'scroll');
        const canScrollY = current.scrollHeight > current.clientHeight + 1 && (overflowY === 'auto' || overflowY === 'scroll');
        const horizontalScrollbarHeight = Math.max(scrollbarHitSize, rect.height - current.clientHeight);
        const verticalScrollbarWidth = Math.max(scrollbarHitSize, rect.width - current.clientWidth);

        if (canScrollX && clientY >= rect.bottom - horizontalScrollbarHeight && clientY <= rect.bottom && clientX >= rect.left && clientX <= rect.right) {
          return true;
        }
        if (canScrollY && clientX >= rect.right - verticalScrollbarWidth && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
          return true;
        }

        if (current === el) break;
        current = current.parentElement;
      }
      return false;
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest?.('[data-event-block]')) return;
      if (isScrollbarPointer(target, e.clientX, e.clientY)) return;
      blankPressPointerId = e.pointerId;
      beginBlankPress(e.clientX, e.clientY);
      grabActive = true;
      grabStartY = e.clientY;
      grabStartScroll = scrollOffset.current;
      el.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (blankPressPointerId !== null && e.pointerId !== blankPressPointerId) return;
      if (blankPressActive) {
        e.preventDefault();
        updateBlankReservation(e.clientX, e.clientY);
        return;
      }
      updateBlankPress(e.clientX, e.clientY);
      if (grabActive && e.pointerType === 'mouse') {
        const dy = grabStartY - e.clientY;
        const newScrollY = Math.max(0, grabStartScroll + dy);
        scrollTo(scrollRef, 0, newScrollY, false);
        scrollOffsetUI.value = newScrollY;
        scrollOffset.current = newScrollY;
      }
    };

    const handlePointerEnd = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (blankPressPointerId !== null && e.pointerId !== blankPressPointerId) return;
      commitBlankReservation();
      clearBlankPress();
      grabActive = false;
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      blankPressPointerId = null;
      el.style.cursor = 'grab';
    };

    const handlePointerCancel = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (blankPressPointerId !== null && e.pointerId !== blankPressPointerId) return;
      cancelBlankReservation();
      clearBlankPress();
      grabActive = false;
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      blankPressPointerId = null;
      el.style.cursor = 'grab';
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        if (beginPinchZoom(e.touches)) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      if (e.touches.length !== 1) return;
      const target = e.target as HTMLElement;
      const touch = e.touches[0];
      blankPressTouchId = touch.identifier;
      touchSwipeStartX = touch.clientX;
      touchSwipeStartY = touch.clientY;
      touchSwipeLastX = touch.clientX;
      touchSwipeLastY = touch.clientY;
      touchSwipeActive = false;
      if (!target.closest?.('[data-event-block]')) {
        beginBlankPress(touch.clientX, touch.clientY);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchPinchActive || e.touches.length >= 2) {
        if (!touchPinchActive && e.touches.length >= 2) beginPinchZoom(e.touches);
        const pinchTouches = findPinchTouches(e.touches);
        if (!touchPinchActive || !pinchTouches) return;
        e.preventDefault();
        e.stopPropagation();
        const distance = getTouchDistance(pinchTouches[0], pinchTouches[1]);
        const nextHeight = clampHourHeight(pinchStartHeight * (distance / pinchStartDistance));
        const midpoint = getTouchMidpoint(pinchTouches[0], pinchTouches[1]);
        const viewportY = midpoint.clientY - getWebTimelineViewportTop();
        const nextScrollY = Math.max(0, TOP_PAD + pinchFocalMinutes * nextHeight - viewportY);
        applyPinchZoom(nextHeight, nextScrollY);
        return;
      }

      const touch = findTouch(e.touches, blankPressTouchId);
      if (!touch) return;
      touchSwipeLastX = touch.clientX;
      touchSwipeLastY = touch.clientY;
      if (blankPressActive) {
        e.preventDefault();
        e.stopPropagation();
        updateBlankReservation(touch.clientX, touch.clientY);
        return;
      }
      const dx = touch.clientX - touchSwipeStartX;
      const dy = touch.clientY - touchSwipeStartY;
      if (!hasHorizontalOverflow && !touchSwipeActive && Math.abs(dx) >= TIMELINE_SWIPE_ACTIVATE_PX && Math.abs(dx) > Math.abs(dy) * TIMELINE_SWIPE_AXIS_RATIO) {
        touchSwipeActive = true;
        clearBlankPress();
        grabActive = false;
        lockBrowserTouch();
      }
      if (touchSwipeActive) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      updateBlankPress(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchPinchActive) {
        e.preventDefault();
        e.stopPropagation();
        finishPinchZoom();
        blankPressTouchId = null;
        resetTouchSwipe();
        return;
      }

      const touch = findTouch(e.changedTouches, blankPressTouchId);
      if (!touch) return;
      const wasBlankPressActive = blankPressActive;
      const wasTouchSwipeActive = touchSwipeActive;
      const translationX = (touch.clientX || touchSwipeLastX) - touchSwipeStartX;
      const translationY = (touch.clientY || touchSwipeLastY) - touchSwipeStartY;
      if (wasBlankPressActive || wasTouchSwipeActive) {
        e.preventDefault();
        e.stopPropagation();
      }
      commitBlankReservation();
      if (!wasBlankPressActive && wasTouchSwipeActive) {
        handleTimelineSwipeEnd(translationX, translationY);
      }
      clearBlankPress();
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      unlockBrowserTouch();
      blankPressTouchId = null;
      resetTouchSwipe();
    };

    const handleTouchCancel = (e: TouchEvent) => {
      if (touchPinchActive) {
        finishPinchZoom();
        blankPressTouchId = null;
        resetTouchSwipe();
        return;
      }

      const touch = findTouch(e.changedTouches, blankPressTouchId);
      if (!touch) return;
      cancelBlankReservation();
      clearBlankPress();
      setDragCreateScrollLocked(false);
      stopInteractionEdges();
      unlockBrowserTouch();
      blankPressTouchId = null;
      resetTouchSwipe();
    };

    el.style.cursor = 'grab';
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('pointerdown', handlePointerDown);
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerCancel);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: false });
    return () => {
      if (pinchFrame != null) cancelAnimationFrame(pinchFrame);
      cancelBlankReservation();
      clearBlankPress();
      unlockBrowserTouch();
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerCancel);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [onDragCreate, handleDragEdge, stopInteractionEdges, updateDragCreateLabel, triggerDragCreate, clearDragCreate, handleTimelineSwipeEnd, hasHorizontalOverflow, timelineBottomPadding]);

  const timelineBody = (
    <Animated.View
      style={[
        { width: timelineContentWidth, backgroundColor: colors.surface },
        timelineBodySizeStyle,
      ]}
    >
      {/* Hour rows */}
      <Animated.View style={[s.hourGrid, { height: 24 * MAX_HOUR_HEIGHT }, hourGridOffsetStyle]} pointerEvents="none">
        <View style={s.hourGridSurface}>
          {Array.from({ length: 24 }, (_, h) => (
            <HourLine
              key={h}
              h={h}
              animatedHourHeight={animatedHourHeight}
              styles={s}
            />
          ))}
        </View>
        <HourLabels
          animatedHourHeight={animatedHourHeight}
          styles={s}
        />
      </Animated.View>

      {/* Event blocks — side-by-side when overlapping */}
      <Animated.View style={[s.eventLayerOffset, eventLayerOffsetStyle]}>
      <View style={[s.eventLayerSurface, { height: totalHeight }]}>
      <Pressable
        accessible={false}
        onPress={clearEventSelection}
        style={s.selectionDismissArea}
      />
      {positioned.map((p, idx) => {
        const visibleTotalAvail = Math.max(0, availableWidth - EVENT_LEFT - EVENT_RIGHT - EVENT_SIDE_INSET * 2);
        const totalCols = p.totalCols || 1;
        const readableTotalAvail = totalCols * EVENT_MIN_COLUMN_WIDTH + Math.max(0, totalCols - 1) * EVENT_GAP;
        const totalAvail = Math.max(visibleTotalAvail, readableTotalAvail);
        let colW = (totalAvail - (totalCols - 1) * EVENT_GAP) / totalCols;
        let colLeft = EVENT_LEFT + EVENT_SIDE_INSET + (p.col || 0) * (colW + EVENT_GAP);

        if (p.isDragClone) {
          return (
            <DragPreviewEventBlock
              key={`drag-clone-${selectedDate}-${p.event.seq ?? idx}`}
              p={p}
              colLeft={colLeft}
              colW={colW}
            />
          );
        }

        if (p.isGroup) {
          return (
            <GroupedEventBlock
              // A group key such as `start-540` commonly repeats on adjacent
              // days. Reusing that native view carries its previous width and
              // column animation into the newly landed page, making reminders
              // visibly resettle after the swipe. Each day owns its geometry.
              key={`group-${selectedDate}-${p.groupKey ?? idx}`}
              p={p}
              colLeft={colLeft}
              colW={colW}
              isExpanded={expandedGroupKey === p.groupKey}
              selectedSeq={selectedSeq}
              onToggle={(groupKey) => setExpandedGroupKey(current => current === groupKey ? null : groupKey)}
              onSelect={(seq) => setSelectedSeq(seq ?? null)}
              onPress={onPressEvent}
              onMoveToMinutes={handleGroupItemMove}
              onMoveGroupToMinutes={handleGroupBatchMove}
              activeGroupItemDragSeq={activeGroupItemDragSeq}
              onDragStart={handleEventDragStart}
              onItemDragStart={handleGroupItemDragStart}
              onDragUpdate={handleGroupItemDragUpdate}
              onGroupDragStart={handleGroupDragStart}
              onGroupDragUpdate={handleGroupDragUpdate}
              onDragFinish={finishGroupItemDrag}
              onDragEdge={handleDragEdge}
              onDragEdgeStop={stopInteractionEdges}
              pointerYToMinutes={pointerYToSnappedMinutes}
              scrollOffsetUI={scrollOffsetUI}
              scrollNativeGesture={isWeb ? undefined : scrollNativeGesture}
              hourHeight={hourHeight}
              animatedHourHeight={animatedHourHeight}
              zoomContentOffsetUI={isWeb ? pinchContentOffsetY : undefined}
            />
          );
        }

        return (
          <EventBlock
            // Recurring occurrences can share one seq across days. Remount the
            // visual block at the destination geometry instead of springing it
            // from the occurrence that belonged to the previous day.
            key={`event-${selectedDate}-${p.event.seq ?? idx}`}
            p={p}
            colLeft={colLeft}
            colW={colW}
            isSelected={selectedSeq === p.event.seq}
            onSelect={(seq) => setSelectedSeq(seq ?? null)}
            onPress={onPressEvent}
            onResizeEvent={onResizeEvent}
            onMoveToMinutes={handleEventMove}
            onDragUpdate={handleDragUpdate}
            onDragClear={handleDragClear}
            onDragStart={handleEventDragStart}
            onDragEdge={handleDragEdge}
            onDragEdgeStop={stopInteractionEdges}
            scrollOffsetUI={scrollOffsetUI}
            scrollNativeGesture={isWeb ? undefined : scrollNativeGesture}
            selectedDate={selectedDate}
            hourHeight={hourHeight}
            totalHeight={totalHeight}
            animatedHourHeight={animatedHourHeight}
            zoomContentOffsetUI={isWeb ? pinchContentOffsetY : undefined}
          />
        );
      })}

      {/* Drag-to-create ghost block */}
      <Animated.View style={[s.dragCreateGhost, { width: visibleEventLaneWidth }, dragCreateGhostStyle]} pointerEvents="none">
        {dragCreateLabel && (
          <View style={s.dragCreateLabelWrap}>
            <Text style={s.dragCreateLabelText}>{dragCreateLabel}</Text>
          </View>
        )}
        <Text style={s.dragCreateHint}>New reminder</Text>
      </Animated.View>

      {/* Red "now" line — only when viewing today */}
      {isToday && (
        <NowLine
          topPad={TOP_PAD}
          totalHeight={totalHeight}
          hourHeight={hourHeight}
          animatedHourHeight={animatedHourHeight}
          zoomContentOffsetUI={isWeb ? pinchContentOffsetY : undefined}
        />
      )}
      </View>
      </Animated.View>
    </Animated.View>
  );

  const scrollView = (
    <AnimatedScrollView
      ref={scrollRef}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      style={[s.container, { width: timelineContentWidth }]}
      contentOffset={{ x: 0, y: initialScrollOffset.current ?? 0 }}
      showsVerticalScrollIndicator={false}
      bounces={false}
      overScrollMode="never"
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
    >
      {isWeb ? timelineBody : (
        <GestureDetector gesture={dragCreateGesture}>
          {timelineBody}
        </GestureDetector>
      )}
    </AnimatedScrollView>
  );

  const verticalScrollHost = isWeb ? scrollView : (
    <GestureDetector gesture={scrollNativeGesture}>
      {scrollView}
    </GestureDetector>
  );

  const horizontalScrollView = hasHorizontalOverflow ? (
    <ScrollView
      horizontal
      style={s.horizontalScroll}
      contentContainerStyle={[s.horizontalScrollContent, { width: timelineContentWidth }]}
      showsHorizontalScrollIndicator
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      directionalLockEnabled
    >
      <View style={[s.horizontalScrollInner, { width: timelineContentWidth }]}>
        {verticalScrollHost}
      </View>
    </ScrollView>
  ) : (
    <View style={s.horizontalScroll}>
      {verticalScrollHost}
    </View>
  );

  const scrollContent = (
    <View ref={scrollContainerRef} style={s.timelineViewport} onLayout={handleContainerLayout}>
    <Animated.View
      renderToHardwareTextureAndroid
      shouldRasterizeIOS
      style={[s.currentTimelinePage, currentTimelinePageStyle]}
    >
      {horizontalScrollView}
    </Animated.View>
    {swipePreviewDirection != null ? (
      <TimelineSwipePreview
        events={swipePreviewEvents}
        hourHeight={hourHeight}
        animatedHourHeight={animatedHourHeight}
        totalHeight={MAX_HOUR_HEIGHT * 24 + TOP_PAD + timelineBottomPadding}
        width={availableWidth}
        tagColors={tagColors}
        columnOverrides={localColPrefs}
        styles={s}
        pageStyle={swipePreviewPageStyle}
        contentOffsetStyle={swipePreviewContentOffsetStyle}
      />
    ) : null}
    {dragEdgeDirection !== null && (
      <View
        pointerEvents="none"
        style={[
          s.edgeHint,
          dragEdgeDirection < 0 ? s.edgeHintLeft : s.edgeHintRight,
        ]}
      >
        {dragEdgeDirection < 0 && <Ionicons name="chevron-back" size={15} color="#fff" />}
        <Text style={s.edgeHintText}>{dragEdgeDirection < 0 ? 'Previous day' : 'Next day'}</Text>
        {dragEdgeDirection > 0 && <Ionicons name="chevron-forward" size={15} color="#fff" />}
      </View>
    )}
    </View>
  );

  // On web: skip GestureDetector (zoom handled via wheel listener), use plain wrapper
  // On native: wrap with GestureDetector for pinch-to-zoom
  if (isWeb) {
    return (
      <View ref={webContainerRef} style={{ flex: 1 }}>
        {scrollContent}
      </View>
    );
  }

  return (
    <GestureDetector gesture={nativeTimelineGesture}>
      <Animated.View style={{ flex: 1 }}>
        {scrollContent}
      </Animated.View>
    </GestureDetector>
  );
}

// ── Styles ──────────────────────────────────────────────────
const createStyles = (colors: ThemeColors) => StyleSheet.create({
  timelineViewport: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  currentTimelinePage: {
    flex: 1,
    overflow: 'hidden',
  },
  swipePreviewViewport: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  swipePreviewContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.surface,
  },
  swipePreviewPositionedEvent: {
    position: 'absolute',
    overflow: 'visible',
  },
  swipePreviewEventInner: {
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 3,
    overflow: 'hidden',
  },
  swipePreviewGroupInner: {
    borderLeftWidth: 3,
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    overflow: 'hidden',
    justifyContent: 'center',
  },
  swipePreviewGroupTime: {
    position: 'absolute',
    bottom: 2,
    color: colors.textSecondary,
  },
  horizontalScroll: {
    flex: 1,
  },
  horizontalScrollContent: {
    flexGrow: 1,
  },
  horizontalScrollInner: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    borderRadius: 22,
    ...(Platform.OS === 'web'
      ? {
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
        }
      : { overflow: 'hidden' }),
  } as any,
  hourGrid: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: TOP_PAD,
    height: 24 * DEFAULT_HOUR_HEIGHT,
  },
  hourGridSurface: {
    ...StyleSheet.absoluteFillObject,
  },
  eventLayerOffset: {
    ...StyleSheet.absoluteFillObject,
  },
  eventLayerSurface: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
  },
  selectionDismissArea: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
  hourRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  hourLabelsText: {
    position: 'absolute',
    left: 0,
    top: -7,
    width: GUTTER_WIDTH,
    paddingRight: 8,
    textAlign: 'right',
    fontSize: 10,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  hourLineGutter: {
    width: GUTTER_WIDTH,
  },
  hourLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border,
  },
  eventBlock: {
    position: 'absolute',
    borderLeftWidth: 3,
    borderRadius: 8,
    overflow: 'visible',
    zIndex: 1,
    ...(Platform.OS === 'web'
      ? {
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }
      : null),
  },
  eventBlockInner: {
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    overflow: 'hidden',
  },
  eventBlockSelected: {
    zIndex: 5,
    overflow: 'visible',
    elevation: 6,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  },
  eventBlockSelectedCompact: {
    zIndex: 5,
    overflow: 'visible',
    elevation: 7,
    shadowColor: '#000000',
    shadowOpacity: 0.22,
    shadowRadius: 7,
    shadowOffset: { width: 0, height: 2 },
  },
  eventContent: {
    flex: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    justifyContent: 'center',
  },
  eventContentStable: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    justifyContent: 'flex-start',
  },
  eventContentCompact: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    justifyContent: 'center',
  },
  eventTitle: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
  },
  eventTitleCompact: {
    fontSize: 11,
    fontWeight: '600',
    lineHeight: 14,
  },
  eventTitleTiny: {
    fontSize: 9,
    fontWeight: '600',
    lineHeight: 11,
  },
  eventContentTiny: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 0,
    justifyContent: 'center',
  },
  eventTimeInline: {
    fontSize: 10,
    fontWeight: '400',
    color: colors.textSecondary,
  },
  eventTime: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: 2,
  },
  groupBlockInner: {
    borderLeftWidth: 3,
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    overflow: 'hidden',
    paddingHorizontal: 8,
    paddingVertical: 5,
    justifyContent: 'center',
  },
  groupBlockInnerCompact: {
    paddingVertical: 2,
  },
  groupHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  groupTitle: {
    flex: 1,
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 14,
  },
  groupTitleCompact: {
    fontSize: 10,
    lineHeight: 12,
  },
  groupCountBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  groupCountBadgeCompact: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
  },
  groupCountText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },
  groupTimeText: {
    marginTop: 2,
    fontSize: 10,
    color: colors.textSecondary,
  },
  groupPopover: {
    position: 'absolute',
    borderRadius: 8,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: colors.border,
    padding: 6,
    gap: 5,
    zIndex: 90,
    overflow: 'visible',
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#000',
          shadowOpacity: 0.16,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 4 },
        }
      : { elevation: 6 }),
  },
  groupRow: {
    position: 'relative',
    minHeight: 42,
    borderRadius: 7,
    overflow: 'visible',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  groupRowSelected: {
    borderWidth: 2,
    borderColor: colors.primary,
    backgroundColor: colors.primary + '0F',
    elevation: 4,
    shadowColor: '#000000',
    shadowOpacity: 0.16,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
  groupRowColor: {
    width: 3,
    borderTopLeftRadius: 7,
    borderBottomLeftRadius: 7,
  },
  groupRowPressable: {
    flex: 1,
    minHeight: 40,
    paddingHorizontal: 8,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  groupRowMain: {
    flex: 1,
    minWidth: 0,
  },
  groupRowTitle: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 15,
  },
  groupRowTime: {
    marginTop: 1,
    fontSize: 10,
    color: colors.textSecondary,
  },
  groupDragLabel: {
    position: 'absolute',
    top: -28,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 100,
  },
  nowLineWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 10,
  },
  nowDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#EA4335',
    marginLeft: GUTTER_WIDTH - 5,
  },
  nowLine: {
    flex: 1,
    height: 2,
    backgroundColor: '#EA4335',
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 28,
    zIndex: 80,
    elevation: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizeGrip: {
    width: 36,
    height: 5,
    borderRadius: 3,
    backgroundColor: colors.textSecondary,
    borderWidth: 1,
    borderColor: '#FFFFFF',
    shadowColor: '#000000',
    shadowOpacity: 0.2,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
  },
  dragTimeLabel: {
    position: 'absolute',
    top: -26,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  dragTimeLabelText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
    backgroundColor: colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    overflow: 'hidden',
  },
  edgeHint: {
    position: 'absolute',
    top: '44%',
    zIndex: 80,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: colors.primary + 'E6',
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: colors.primary,
          shadowOpacity: 0.3,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 4 },
        }
      : { elevation: 5 }),
  },
  edgeHintLeft: {
    left: 10,
  },
  edgeHintRight: {
    right: 10,
  },
  edgeHintText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '800',
  },
  dragCreateGhost: {
    position: 'absolute',
    left: EVENT_LEFT + EVENT_SIDE_INSET,
    right: EVENT_RIGHT,
    backgroundColor: colors.primary + '18',
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    borderRadius: 8,
    borderStyle: 'dashed',
    justifyContent: 'center',
    paddingHorizontal: 10,
    zIndex: 30,
  },
  dragCreateLabelWrap: {
    position: 'absolute',
    top: -26,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  dragCreateLabelText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
    backgroundColor: colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    overflow: 'hidden',
  },
  dragCreateHint: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
    opacity: 0.7,
  },
});
